import type { en } from './en.ts';

/**
 * Thai message catalogue.
 *
 * Typed against the English one, so a key added there and forgotten here is a
 * compile error rather than a blank button somebody finds in production.
 *
 * Written as Thai, not translated word for word. A few choices worth
 * recording: buttons use no polite particle (a button is a label, not a
 * sentence), "เพื่อน" is used for a connection rather than the literal
 * "การเชื่อมต่อ", and Thai has no plural form — `Intl.PluralRules` returns
 * `other` for every count, which is why each plural key here has only that
 * form.
 */
export const th: Record<keyof typeof en, string> = {
  /* Navigation and shell */
  'nav.home': 'หน้าแรก',
  'nav.discover': 'ค้นหาเพื่อน',
  'nav.friends': 'เพื่อน',
  'nav.groups': 'กลุ่ม',
  'nav.messages': 'ข้อความ',
  'nav.notifications': 'การแจ้งเตือน',
  'nav.settings': 'ตั้งค่า',
  'nav.moderation': 'ดูแลระบบ',
  'nav.profile': 'โปรไฟล์',
  'nav.signOut': 'ออกจากระบบ',
  'nav.settingsPrivacy': 'ตั้งค่าและความเป็นส่วนตัว',
  'nav.search': 'ค้นหาใน TalkVibe',
  'nav.skipToContent': 'ข้ามไปยังเนื้อหา',
  'nav.yourInterests': 'ความสนใจของคุณ',
  'nav.contacts': 'รายชื่อ',
  'nav.connectionRequests': 'คำขอเป็นเพื่อน',
  'nav.wantsToConnect': 'ขอเป็นเพื่อน',
  'nav.noContacts': 'ยังไม่มีรายชื่อ',
  'nav.findPeople': 'หาเพื่อน',
  'nav.tagline': 'TalkVibe · คุยกับคนทั่วโลก อ่านเป็นภาษาของคุณเอง',
  'nav.myProfile': 'โปรไฟล์ของฉัน',
  'nav.followersLabel': 'ผู้ติดตาม',
  'nav.followingLabel': 'กำลังติดตาม',
  'nav.quickNav': 'ไปที่',
  'nav.myGroups': 'กลุ่มของฉัน',
  'nav.newsFeed': 'ฟีดข่าว',
  'nav.manage': 'จัดการ',
  'nav.verifyEmail': 'ยืนยันอีเมลของคุณเพื่อความปลอดภัยของบัญชี',
  'nav.verifySend': 'ส่งลิงก์',
  'nav.dismiss': 'ปิด',
  'nav.accountMenu': 'เมนูบัญชี',
  'nav.noGroups': 'ยังไม่มีกลุ่ม',
  'nav.members.one': 'สมาชิก {count} คน',
  'nav.members.other': 'สมาชิก {count} คน',
  'nav.whoToMeet': 'คนที่คุณอาจอยากรู้จัก',
  'nav.suggested': 'คนที่น่าสนใจ',
  'common.soon': 'เร็วๆ นี้',
  'common.notReadyYet': 'ยังไม่ได้สร้างระบบ แต่หน้าจอเตรียมที่ไว้แล้ว',
  'nav.marketplace': 'มาร์เก็ตเพลส',
  'market.title': 'มาร์เก็ตเพลส',
  'market.breadcrumbHome': 'หน้าแรก',
  'market.heading': 'ของขายใกล้คุณ',
  'market.sell': 'ลงขายสินค้าของคุณ',
  'market.searchPlaceholder': 'ค้นหาของในมาร์เก็ตเพลส…',
  'market.areaLabel': 'พื้นที่ค้นหาสินค้า',
  'market.changeArea': 'เปลี่ยน',
  'market.radius': 'ภายใน',
  'market.categories': 'หมวดหมู่สินค้า',
  'market.categoryAll': 'ทั้งหมด',
  'market.categoryTech': 'อุปกรณ์ไอที และแกดเจ็ต',
  'market.categoryComputers': 'คอมพิวเตอร์ และโต๊ะทำงาน',
  'market.categoryFashion': 'เสื้อผ้า และแฟชั่น',
  'market.categoryHome': 'เฟอร์นิเจอร์ และของแต่งบ้าน',
  'market.categoryVehicles': 'ยานยนต์ และอะไหล่',
  'market.categoryBooks': 'หนังสือ และคอร์สเรียน',
  'market.priceRange': 'ช่วงราคา',
  'market.priceMin': 'ต่ำสุด',
  'market.priceMax': 'สูงสุด',
  'market.priceApply': 'ใช้ตัวกรองราคา',
  'market.priceFree': 'ฟรี',
  'market.condition': 'สภาพสินค้า',
  'market.conditionNew': 'ของใหม่ ยังไม่แกะกล่อง',
  'market.conditionLikeNew': 'ใช้งานน้อยมาก',
  'market.conditionUsed': 'มือสอง ใช้งานได้ปกติ',
  'market.sellerType': 'ผู้ขาย',
  'market.sellerVerified': 'ผู้ขายที่ยืนยันตัวตนแล้ว',
  'market.sellerOfficial': 'ร้านค้าทางการ',
  'market.sortBy': 'เรียงตาม',
  'market.sortRecent': 'ใหม่ล่าสุด',
  'market.sortPriceAsc': 'ราคา: ต่ำไปสูง',
  'market.sortPriceDesc': 'ราคา: สูงไปต่ำ',
  'market.sortNearest': 'ใกล้ที่สุดก่อน',
  'market.viewGrid': 'มุมมองตาราง',
  'market.viewList': 'มุมมองรายการ',
  'market.found': '{count} รายการ',
  'market.loadMore': 'ดูสินค้าเพิ่มเติม',
  'market.showing': 'แสดง {shown} จาก {total}',
  'market.mapTitle': 'ดูสินค้าตามจุดนัดรับ',
  'market.mapExpand': 'ขยายแผนที่เต็มจอ',
  'market.safety': 'ซื้อขายอย่างปลอดภัย',
  'market.safetyBody': 'ตรวจสอบว่าคุณกำลังคุยกับใคร นัดเจอในที่สาธารณะ และดูของจริงก่อนจ่ายเงินทุกครั้ง',
  'market.emptyTitle': 'ยังไม่มีของขาย',
  'market.emptyBody': 'ยังไม่มีใครลงขายอะไรที่นี่ ถ้าคุณมีของจะขาย คุณเป็นคนแรกได้เลย',
  'market.save': 'บันทึกรายการนี้',
  'market.categorySports': 'กีฬาและกิจกรรมกลางแจ้ง',
  'market.categoryMusic': 'เครื่องดนตรีและเครื่องเสียง',
  'market.categoryToys': 'ของเล่นและเกม',
  'market.categoryOther': 'อื่นๆ ทั้งหมด',
  'market.sold': 'ขายแล้ว',
  'market.sellerGone': 'ผู้ขายออกจากระบบไปแล้ว',
  'market.unsave': 'เอาออกจากรายการที่บันทึก',
  'market.foundMore': 'พบแล้ว {count} รายการ',
  'market.failedTitle': 'โหลดมาร์เก็ตเพลสไม่สำเร็จ',
  'market.failedBody': 'มีอะไรผิดพลาดระหว่างดึงรายการสินค้า ลองอีกครั้งในอีกสักครู่',
  'market.noMatchTitle': 'ไม่มีรายการที่ตรงกับที่เลือก',
  'market.noMatchBody': 'ไม่มีสินค้าที่ตรงกับคำค้น หมวดหมู่ ช่วงราคา หรือสภาพที่เลือกไว้ ลองผ่อนเงื่อนไขข้อใดข้อหนึ่งแล้วดูอีกครั้ง',
  'market.backToMarket': 'กลับไปที่มาร์เก็ตเพลส',
  'market.sellTitle': 'ลงขายสินค้า',
  'market.formTitleLabel': 'คุณกำลังขายอะไร',
  'market.formTitlePlaceholder': 'คีย์บอร์ดแมคคานิคอล ใช้งานน้อยมาก',
  'market.formDescription': 'รายละเอียด',
  'market.formDescriptionPlaceholder': 'บอกว่าเป็นของอะไร ใช้มานานเท่าไร และอะไรที่ผู้ซื้อควรรู้ก่อนมาดูของ',
  'market.formCategory': 'หมวดหมู่',
  'market.formCondition': 'สภาพสินค้า',
  'market.formPriceHeading': 'ราคา',
  'market.formFree': 'ยกให้ฟรี',
  'market.formPrice': 'ราคาที่ตั้งขาย',
  'market.formPricePlaceholder': '1800',
  'market.formWasPrice': 'ลดจากราคา',
  'market.formWasPricePlaceholder': 'ถ้าไม่ได้ลดราคา เว้นว่างไว้',
  'market.formPhotoHeading': 'รูปภาพ',
  'market.formPhotoRemove': 'เอารูปออก',
  'market.photoCover': 'หน้าปก',
  'market.photoMakeCover': 'ตั้งเป็นหน้าปก',
  'market.photoRemoveOne': 'ลบรูปที่ {n}',
  'market.photoShow': 'ดูรูปที่ {n}',
  'market.photoLimit': 'หนึ่งรายการใส่รูปได้ {count} รูป',
  'market.photosFailed': 'สร้างรายการแล้ว แต่บางรูปอัปโหลดไม่สำเร็จ ({which}) เปิดรายการนั้นแล้วใส่อีกครั้ง',
  'market.formPhotoHint': 'ใส่ได้สูงสุด {count} รูป รูปแรกคือรูปหน้าปกที่ผู้ซื้อเห็นในตาราง ระบบจะอัปโหลดให้หลังสร้างรายการแล้ว',
  'market.formArea': 'นัดรับของได้ที่ไหน',
  'market.formAreaPlaceholder': 'แถว BTS อโศก',
  'market.formAreaHint': 'เขียนแบบที่คุณจะบอกเพื่อนก็ได้ ระบบนี้ไม่มีแผนที่อยู่ข้างหลัง เขตหรือชื่อสถานีจึงมีประโยชน์กว่าที่อยู่เต็ม',
  'market.sellNotice': 'TalkVibe ไม่ได้ดูแลเรื่องเงินและไม่ได้ตรวจสอบตัวตนของใคร รายการของคุณคือประกาศขาย ส่วนที่เหลือเป็นเรื่องระหว่างคุณกับผู้ซื้อ',
  'market.formPublish': 'เผยแพร่ประกาศขาย',
  'market.formPublishing': 'กำลังเผยแพร่…',
  'market.photoFailed': 'สร้างรายการแล้ว แต่อัปโหลดรูปไม่สำเร็จ เปิดรายการนั้นแล้วลองใส่รูปอีกครั้ง',
  'market.previewHeading': 'หน้าตาเวลาแสดงผล',
  'market.previewTitleEmpty': 'รายการของคุณ',
  'market.goneTitle': 'ประกาศนี้ถูกเอาลงแล้ว',
  'market.goneBody': 'ผู้ขายเอาลงไปแล้ว ลิงก์ไม่ได้เสีย เพียงแต่ไม่มีของให้ซื้อ',
  'market.missingTitle': 'ไม่พบประกาศนี้',
  'market.missingBody': 'ลิงก์นี้ไม่ได้ชี้ไปที่อะไร ลองตรวจดูอีกครั้ง หรือเลือกดูในมาร์เก็ตเพลสแทน',
  'market.noDescription': 'ผู้ขายไม่ได้เขียนรายละเอียดไว้',
  'market.sellerHeading': 'ผู้ขาย',
  'market.viewSellerProfile': 'เปิดโปรไฟล์ผู้ขาย',
  'market.viewItem': 'ดูสินค้า',
  'market.yourChats': 'แชทในตลาดของคุณ',
  'market.listingGone': 'สินค้าที่ถูกเอาออกแล้ว',
  'market.messageSeller': 'คุยกับผู้ขาย',
  'market.notForSale': 'ไม่ได้ขายแล้ว',
  'market.messageSellerFailed': 'เปิดแชทไม่สำเร็จ',
  'market.contactBody': 'แชทนี้เป็นเรื่องสินค้าชิ้นนี้เท่านั้น และแยกออกจากกล่องข้อความปกติของคุณ TalkVibe ไม่มีระบบชำระเงิน ตกลงวิธีจ่ายและนัดรับกันเองกับผู้ขาย',
  'market.manageHeading': 'รายการของคุณ',
  'market.markSold': 'ทำเครื่องหมายว่าขายแล้ว',
  'market.markAvailable': 'นำกลับมาขายอีกครั้ง',
  'market.removeListing': 'เอาประกาศลง',
  'market.removeConfirm': 'เอาประกาศนี้ลงหรือไม่ รายการจะหายไปจากมาร์เก็ตเพลสและนำกลับมาไม่ได้',
  'nav.pages': 'เพจ',
  'nav.games': 'เกม',
  'nav.bookmarks': 'บุ๊กมาร์ก',
  'bookmarks.lede': 'โพสต์ที่คุณบันทึกไว้ มีแค่คุณที่เห็นรายการนี้',
  'bookmarks.emptyTitle': 'ยังไม่ได้บันทึกอะไรไว้',
  'bookmarks.emptyBody': 'กดบันทึกจากเมนู ⋯ บนโพสต์ แล้วมันจะมาอยู่ที่นี่',
  'nav.events': 'งานอีเวนต์',
  'nav.media': 'ไฟล์มีเดีย',
  'nav.favouriteGroups': 'กลุ่มโปรด',
  'home.filterForYou': 'สำหรับคุณ',
  'home.filterPopular': 'ยอดนิยม',
  'home.tuneFeed': 'ปรับแต่งฟีด',
  'composer.photo': 'รูปภาพ',
  'composer.removePhoto': 'เอารูปนี้ออก',
  'composer.clearFeeling': 'ล้างความรู้สึก',
  'composer.untag': 'เอาแท็ก {name} ออก',
  'composer.pollOption': 'ตัวเลือกที่ {n}',
  'composer.attachmentsDropped': 'โพสต์ออกไปแล้ว แต่ TalkVibe ไม่ได้บันทึก: {what} ลองเพิ่มใหม่อีกครั้งในอีกสักครู่',
  'composer.addOption': 'เพิ่มตัวเลือก',
  'composer.removeOption': 'เอาตัวเลือกนี้ออก',
  'composer.removePoll': 'เอาโพลล์ออก',
  'composer.pollMultiple': 'ตอบได้มากกว่าหนึ่งข้อ',
  'composer.pollCloses': 'ปิดโหวต',
  'composer.pollNeverCloses': 'ไม่ปิด',
  'composer.pollDays.one': '{count} วัน',
  'composer.pollDays.other': '{count} วัน',
  'composer.tagFriendsOnly': 'แท็กได้เฉพาะคนที่เชื่อมต่อกับคุณแล้ว',
  'post.isFeeling': 'รู้สึก{feeling}',
  'post.with': 'กับ',
  'post.onProfileOf': 'บนโปรไฟล์ของ {name}',
  'post.votes.one': '{count} โหวต',
  'post.votes.other': '{count} โหวต',
  'post.openPhoto': 'เปิดรูปที่ {n} จาก {total}',
  'post.photoViewer': 'รูปในโพสต์นี้',
  'post.previousPhoto': 'รูปก่อนหน้า',
  'post.nextPhoto': 'รูปถัดไป',
  'post.photoCounter': '{n} จาก {total}',
  'post.sharePost': 'แชร์โพสต์',
  'post.reportPost': 'รายงานโพสต์นี้',
  'post.photoComments': 'ความคิดเห็นของรูปนี้',
  'post.pollClosed': 'ปิดโหวตแล้ว',
  'post.pollMultiple': 'ตอบได้มากกว่าหนึ่งข้อ',
  'feeling.happy': 'มีความสุข',
  'feeling.grateful': 'ขอบคุณ',
  'feeling.excited': 'ตื่นเต้น',
  'feeling.relaxed': 'ผ่อนคลาย',
  'feeling.loved': 'เป็นที่รัก',
  'feeling.proud': 'ภูมิใจ',
  'feeling.tired': 'เหนื่อย',
  'feeling.sad': 'เศร้า',
  'feeling.thoughtful': 'ครุ่นคิด',
  'feeling.motivated': 'ฮึกเหิม',
  'notifications.postMention': '{name} แท็กคุณในโพสต์',
  'composer.photoCount': 'แนบแล้ว {n} จาก {max} รูป',
  'composer.poll': 'โพลล์',
  'composer.feeling': 'ความรู้สึก',
  'composer.tagFriends': 'แท็กเพื่อน',
  'trending.title': 'เทรนด์ยอดนิยม',
  'trending.window': 'กำลังมาแรงตอนนี้ จาก {days} วันที่ผ่านมา',
  'trending.posts': '{count} โพสต์',
  'tag.publicOnly': 'เฉพาะโพสต์ที่ทุกคนอ่านได้ โพสต์ในกลุ่มส่วนตัว บนโปรไฟล์ที่ซ่อนไว้ หรือบนเพจที่ไม่เปิดสาธารณะ จะไม่ปรากฏที่นี่',
  'tag.count': '{count} โพสต์ในหน้านี้',
  'tag.more': 'ดูเพิ่ม',
  'tag.emptyTitle': 'ยังไม่มีใครใช้แท็กนี้',
  'tag.emptyBody': 'ยังไม่มีโพสต์สาธารณะที่ใช้แท็กนี้ ลองเขียนโพสต์ที่มีแท็กนี้แล้วจะมาอยู่ที่นี่',
  'tag.failedTitle': 'โหลดหัวข้อนี้ไม่สำเร็จ',
  'tag.failedBody': 'มีอะไรผิดพลาดระหว่างดึงโพสต์ ลองอีกครั้งในอีกสักครู่',
  'trending.settings': 'ตั้งค่าเทรนด์',
  'trending.empty': 'ยังไม่มีแฮชแท็ก',
  'trending.emptyBody': 'ไม่มีโพสต์ที่ติดแฮชแท็กใน {days} วันที่ผ่านมา ลองใส่แฮชแท็กในโพสต์แล้วมันจะมาอยู่ที่นี่',
  'post.verified': 'บัญชีที่ยืนยันแล้ว',
  'post.repost': 'รีโพสต์',
  'post.views': 'ยอดดู',
  'post.bookmark': 'บันทึก',
  'post.moreActions': 'ตัวเลือกเพิ่มเติม',
  'post.unrepost': 'ยกเลิกรีโพสต์',
  'post.repostGone': 'โพสต์นี้ไม่สามารถแสดงได้แล้ว',
  'post.unbookmark': 'เอาออกจากที่บันทึก',
  'nav.refresh': 'สุ่มใหม่',
  'nav.seeMore': 'ดูเพิ่มเติม',
  'home.feedFilter': 'ฟีด',
  'home.filterFollowing': 'กำลังติดตาม',
  'home.filterExplained': 'ฟีดของคุณคือทุกคนที่คุณติดตาม เรียงจากใหม่ไปเก่า ไม่มีการจัดลำดับใหม่',

  /* Authentication */
  'auth.signIn': 'เข้าสู่ระบบ',
  'auth.signUp': 'สมัครสมาชิก',
  'auth.email': 'อีเมล',
  'auth.emailOrUsername': 'อีเมลหรือชื่อผู้ใช้',
  'auth.username': 'ชื่อผู้ใช้',
  'auth.password': 'รหัสผ่าน',
  'auth.currentPassword': 'รหัสผ่านปัจจุบัน',
  'auth.newPassword': 'รหัสผ่านใหม่',
  'auth.forgotPassword': 'ลืมรหัสผ่าน',
  'auth.resetPassword': 'ตั้งรหัสผ่านใหม่',
  'auth.sendResetLink': 'ส่งลิงก์ตั้งรหัสผ่าน',
  'auth.backToSignIn': 'กลับไปเข้าสู่ระบบ',
  'auth.noAccount': 'ยังไม่มีบัญชี',
  'auth.haveAccount': 'มีบัญชีอยู่แล้ว',
  'auth.country': 'ประเทศ',
  'auth.language': 'ภาษาที่คุณอ่าน',
  'auth.languageHelp': 'ข้อความจะถูกแปลเป็นภาษานี้ และหน้าจอจะแสดงเป็นภาษานี้ด้วย',
  'auth.displayName': 'ชื่อที่แสดง',
  'auth.signingIn': 'กำลังเข้าสู่ระบบ…',
  'auth.creating': 'กำลังสร้างบัญชี…',
  'auth.welcomeBack': 'ยินดีต้อนรับกลับมา เข้าสู่ระบบเพื่อใช้งานต่อ',
  'auth.identifierPlaceholder': 'name@example.com หรือ @username',
  'auth.passwordPlaceholder': 'รหัสผ่านของคุณ',
  'auth.showPassword': 'แสดงรหัสผ่าน',
  'auth.hidePassword': 'ซ่อนรหัสผ่าน',
  'auth.secureNote': 'เชื่อมต่อแบบเข้ารหัส และรหัสผ่านไม่ถูกเก็บในรูปแบบที่อ่านได้',
  'auth.resetDone': 'เปลี่ยนรหัสผ่านเรียบร้อยแล้ว เข้าสู่ระบบด้วยรหัสใหม่ได้เลย',
  'auth.signInFailed': 'เข้าสู่ระบบไม่สำเร็จ ลองอีกครั้ง',
  'auth.orWithEmail': 'หรือเข้าสู่ระบบด้วยอีเมล',
  'auth.loginLede': 'ทุกคนพิมพ์ด้วยภาษาที่ตัวเองคิด และอ่านด้วยภาษาของตัวเอง',

  /* Chat */
  'chat.send': 'ส่ง',
  'chat.placeholder': 'เขียนข้อความ…',
  'chat.messageLabel': 'ข้อความ',
  'chat.addPhoto': 'ส่งรูปภาพ',
  'chat.voiceNote': 'ข้อความเสียง',
  'chat.recordVoice': 'อัดข้อความเสียง',
  'chat.stopRecording': 'หยุดอัด',
  'chat.discardRecording': 'ทิ้งเสียงที่อัดไว้',
  'chat.micBlocked': 'TalkVibe ใช้ไมโครโฟนไม่ได้ อนุญาตในการตั้งค่าเบราว์เซอร์แล้วกดอีกครั้ง',
  'chat.micMissing': 'ไม่พบไมโครโฟน',
  'chat.micUnsupported': 'เบราว์เซอร์นี้อัดเสียงไม่ได้',
  'chat.micBusy': 'มีแอปอื่นใช้ไมโครโฟนอยู่ ปิดแล้วลองใหม่อีกครั้ง',
  'chat.micFailed': 'เปิดไมโครโฟนไม่สำเร็จ ({reason})',
  'chat.playVoice': 'เล่นข้อความเสียง',
  'chat.pauseVoice': 'หยุดชั่วคราว',
  'chat.voicePreparing': 'กำลังเตรียม…',
  'chat.voiceFailed': 'เตรียมไฟล์เสียงนี้ไม่สำเร็จ',
  'chat.sending': 'กำลังส่ง…',
  'chat.readBy': 'อ่านแล้ว {count} คน',
  'chat.readByTitle': 'มี {count} คนอ่านข้อความนี้แล้ว',
  'chat.read': 'อ่านแล้ว',
  'chat.removePhoto': 'เอารูปออก',
  'chat.emoji': 'อิโมจิ',
  'chat.photo': 'รูปภาพ',
  'chat.formerMember': 'สมาชิกที่ออกไปแล้ว',
  'chat.photoExpanded': 'รูปภาพ ขยายเต็มจอ',
  'chat.openPhoto': 'เปิดรูป',
  'chat.replyTo': 'ตอบกลับ',
  'chat.replyingTo': 'กำลังตอบกลับ',
  'chat.cancelReply': 'ยกเลิกการตอบกลับ',
  'chat.replyGone': 'ข้อความที่ตอบกลับถูกลบแล้ว',
  'chat.likeMessage': 'ถูกใจ',
  'chat.deleteMessage': 'ลบข้อความ',

  'emoji.faces': 'หน้ายิ้ม',
  'emoji.feelings': 'อารมณ์',
  'emoji.gestures': 'มือ',
  'emoji.hearts': 'หัวใจ',
  'emoji.people': 'ผู้คน',
  'emoji.nature': 'ธรรมชาติ',
  'emoji.food': 'อาหาร',
  'emoji.objects': 'สิ่งของ',
  'emoji.more': 'คีย์บอร์ดของคุณพิมพ์อิโมจิอื่นลงในช่องได้ทุกตัว',
  'chat.typeMessage': 'พิมพ์ข้อความ',
  'chat.typing': 'กำลังพิมพ์…',
  'chat.online': 'ออนไลน์',
  'chat.offline': 'ออฟไลน์',
  'chat.back': 'กลับไปหน้าข้อความ',
  'chat.noMessages': 'ยังไม่มีข้อความ',
  'chat.startConversation': 'ทักทายเพื่อเริ่มคุยกันได้เลย',
  'chat.conversationStart': 'นี่คือจุดเริ่มต้นของการสนทนา',
  'chat.viewOriginal': 'ดูข้อความต้นฉบับ',
  'chat.hideOriginal': 'ซ่อนต้นฉบับ',
  'chat.translatedFrom': 'แปลจาก{language}',
  'chat.messageDeleted': 'ข้อความถูกลบแล้ว',
  'chat.delete': 'ลบ',
  'chat.retry': 'ลองใหม่',
  'chat.discard': 'ทิ้ง',
  'chat.failedToSend': 'ส่งไม่สำเร็จ',
  'chat.loadOlder': 'ดูข้อความเก่ากว่านี้',
  'chat.connecting': 'กำลังเชื่อมต่อ…',
  'chat.reconnecting': 'กำลังเชื่อมต่อใหม่…',
  'chat.offlineNotice': 'ตอนนี้ออฟไลน์อยู่ ข้อความจะถูกส่งเมื่อกลับมาออนไลน์',

  /* Conversations */
  'conversations.title': 'ข้อความ',
  'conversations.filterAll': 'ทั้งหมด',
  'conversations.filterUnread': 'ยังไม่ได้อ่าน',
  'conversations.filterGroups': 'กลุ่ม',
  'conversations.filterStarred': 'สำคัญ',
  'conversations.newChat': 'เขียนข้อความใหม่',
  'conversations.searchPlaceholder': 'ค้นหาในแชทและรายชื่อ',
  'conversations.pickOne': 'เลือกบทสนทนา',
  'conversations.pickOneHelp': 'เลือกจากรายการด้านซ้าย หรือเริ่มคุยใหม่',
  'conversations.friendsOnly': 'ส่งข้อความได้กับคนที่เชื่อมต่อกับคุณแล้ว',
  'friends.blockConfirm': 'บล็อก {name} ไหม เขาจะส่งข้อความหาคุณและหาคุณไม่เจอ',
  'chat.call': 'โทร',
  'chat.video': 'วิดีโอคอล',
  'chat.searchInside': 'ค้นหา',
  'chat.details': 'ข้อมูลการสนทนา',
  'chat.mute': 'ปิดเตือน',
  'chat.save': 'บันทึก',
  'chat.sharedMedia': 'รูปภาพและวิดีโอที่แชร์',
  'chat.sharedFiles': 'ไฟล์เอกสาร',
  /* ---- Calling ---------------------------------------------------------- */
  /* ---- Games ------------------------------------------------------------ */
  'game.title': 'เกม',
  'game.choose': 'เล่น:',
  'game.kind.tictactoe': 'เกม OX',
  'game.kind.connectfour': 'เรียงสี่',
  'game.kind.gomoku': 'เรียงห้า',
  'game.play': 'เล่นเกม',
  'game.yourTurn': 'ตาคุณ',
  'game.theirTurn': '{name} กำลังคิด…',
  'game.youAre': 'คุณคือ {mark}',
  'game.youWon': 'คุณชนะ',
  'game.theyWon': '{name} ชนะ',
  'game.draw': 'เสมอ',
  'game.youResigned': 'คุณยอมแพ้',
  'game.theyResigned': '{name} ยอมแพ้',
  'game.resign': 'ยอมแพ้',
  'game.rematch': 'เล่นอีกครั้ง',
  'game.waiting': 'รออีกฝ่าย',
  'game.finished': 'จบแล้ว',
  'game.listHelp': 'เกมที่คุณกำลังเล่นกับคนที่คุยด้วย',
  'game.noneYet': 'ยังไม่มีเกม',
  'game.noneYetHelp': 'เปิดแชทกับเพื่อน แล้วกดปุ่มเกมเพื่อเริ่มเล่น',
  'game.listFailed': 'โหลดรายการเกมไม่สำเร็จ',
  'game.emptySquare': 'ช่อง {n} ว่าง',
  'game.takenSquare': 'ช่อง {n} {mark}',

  'calls.title': 'โทร',
  'calls.ringing': 'กำลังโทร…',
  'calls.incoming': 'สายเรียกเข้า',
  'calls.connecting': 'กำลังเชื่อมต่อ…',
  'calls.accept': 'รับสาย',
  'calls.decline': 'ปฏิเสธ',
  'calls.hangUp': 'วางสาย',
  'calls.mute': 'ปิดไมค์',
  'calls.unmute': 'เปิดไมค์',
  'calls.mutedNotice': 'ไมค์ของคุณปิดอยู่',
  'calls.ended': 'วางสายแล้ว',
  'calls.declined': 'ปฏิเสธสาย',
  'calls.busy': 'เขากำลังคุยสายอื่น',
  'calls.unanswered': 'ไม่มีผู้รับสาย',
  'calls.failed': 'เชื่อมต่อสายไม่ได้',
  'calls.noMicrophone': 'TalkVibe ต้องขออนุญาตใช้ไมโครโฟน',
  'calls.noRelay': 'การโทรระหว่างบางเครือข่ายต้องใช้เซิร์ฟเวอร์ตัวกลาง ซึ่งยังไม่ได้ตั้งค่า',
  'conversations.none': 'ยังไม่มีการสนทนา',
  'conversations.noneHelp': 'หาใครสักคนคุยด้วย แล้วการสนทนาจะมาแสดงตรงนี้',
  'conversations.noMessagesYet': 'ยังไม่มีข้อความ',
  'conversations.loading': 'กำลังโหลดการสนทนา…',
  'conversations.loadMore': 'ดูเพิ่ม',

  /* Groups */
  'group.create': 'สร้างกลุ่ม',
  'group.privacyChatNote': 'ไม่ว่าเลือกแบบไหน แชทของกลุ่มเป็นของสมาชิกเท่านั้น สาธารณะเปิดเฉพาะโพสต์ ไม่ใช่บทสนทนา',
  'group.visibilityVisibleHelp': 'ทุกคนค้นหากลุ่มนี้เจอผ่านการค้นหาของ TalkVibe',
  'group.visibilityHiddenHelp': 'เจอได้เฉพาะคนที่คุณส่งลิงก์ให้',
  'group.tags': 'แท็ก',
  'group.tagPlaceholder': 'ออกแบบ, การเรียน, กรุงเทพ…',
  'group.addTag': 'เพิ่ม',
  'group.removeTag': 'เอา #{tag} ออก',
  'group.coverChoose': 'เลือกรูป',
  'group.coverChange': 'เลือกรูปอื่น',
  'group.coverHelp': 'อัปโหลดหลังสร้างกลุ่มเสร็จ เปลี่ยนทีหลังได้ตลอด',
  'group.coverRemove': 'เอาภาพหน้าปกออก',
  'group.publicGroup': 'กลุ่มสาธารณะ',
  'group.privateGroup': 'กลุ่มส่วนตัว',
  'group.publicLong': 'ทุกคนที่ล็อกอินอ่านโพสต์ได้ ส่วนแชทยังเป็นของสมาชิกเท่านั้น',
  'group.newTitle': 'สร้างกลุ่มใหม่',
  'group.youAreOwner': 'คุณกำลังสร้างกลุ่มในฐานะผู้ดูแลหลัก',
  'group.owner': 'ผู้ดูแลและผู้ก่อตั้ง',
  'group.nameHelp': 'ใช้ชื่อที่บอกได้ว่ากลุ่มนี้มีไว้ทำอะไร เปลี่ยนทีหลังได้',
  'group.privacy': 'ความเป็นส่วนตัว',
  'group.privacyPublic': 'สาธารณะ',
  'group.privacyPublicHelp': 'ทุกคนจะเห็นสมาชิกและสิ่งที่สมาชิกโพสต์',
  'group.privacyPrivate': 'ส่วนตัว',
  'group.privacyPrivateHelp': 'มีเพียงสมาชิกเท่านั้นที่เห็นสมาชิกและโพสต์',
  'group.visibility': 'ใครค้นหาเจอได้',
  'group.visibilityVisible': 'ค้นหาเจอ',
  'group.visibilityHidden': 'ซ่อนอยู่',
  'group.category': 'หมวดหมู่',
  'group.invite': 'เชิญเพื่อนเข้าร่วมกลุ่ม',
  'group.chosenCount': 'เลือกแล้ว {count} คน',
  'group.removeInvite': 'เอา {name} ออก',
  'group.inviteHelp': 'เชิญได้เฉพาะเพื่อน และกลุ่มหนึ่งรับได้ถึง {max} คน คนที่ยังไม่ได้เชิญเพิ่มทีหลังได้',
  'group.description': 'เกี่ยวกับกลุ่มนี้',
  'group.descriptionPlaceholder': 'กลุ่มนี้มีไว้ทำอะไร และอยากให้สมาชิกปฏิบัติต่อกันอย่างไร',
  'group.preview': 'ตัวอย่าง',
  'group.previewNameEmpty': 'กลุ่มของคุณ',
  'group.previewJoined': 'เข้าร่วมแล้ว',
  'group.previewInvite': 'เชิญเพื่อน',
  'group.cover': 'ภาพหน้าปกกลุ่ม',
  'group.tabAbout': 'เกี่ยวกับ',
  'group.tabMembers': 'สมาชิก',
  'group.tabEvents': 'กิจกรรม',
  'group.tabMedia': 'ไฟล์ & สื่อ',
  'group.aboutTitle': 'เกี่ยวกับกลุ่มนี้',
  'group.membersOnlyLong': 'เฉพาะสมาชิกเท่านั้น คนนอกกลุ่มอ่านสิ่งที่โพสต์ที่นี่ไม่ได้',
  'group.createdBy': 'สร้างเมื่อวันนี้ โดย {name}',
  'group.guidelines': 'กฎระเบียบของกลุ่ม',
  'group.guidelinesHint': 'เคารพสมาชิกทุกคน • ปลอดสแปม',
  'group.rulesPlaceholder': 'บรรทัดละข้อ สมาชิกจะเห็นในหน้ากลุ่ม',
  'group.rulesHelp': 'ไม่ใส่ก็ได้ ผู้ดูแลแก้ภายหลังได้',
  'group.rulesNone': 'กลุ่มนี้ยังไม่ได้ตั้งกฎ',
  'group.rulesTitle': 'กฎระเบียบของกลุ่ม',
  'group.created': 'สร้างเมื่อ {date}',
  'group.noDescription': 'ยังไม่มีคำอธิบาย',
  'group.mediaNone': 'ยังไม่มีรูปที่โพสต์ในกลุ่มนี้',
  'group.membersTitle': 'สมาชิก',
  'group.kind': 'กลุ่มนี้ไว้ทำอะไร',
  'group.kindChat': 'ไว้คุยกัน',
  'group.kindChatHelp': 'เป็นห้องสนทนา จะอยู่ในกล่องข้อความ และเปิดเข้าแชท',
  'group.kindPosts': 'ไว้โพสต์',
  'group.kindPostsHelp': 'เป็นฟีด จะไม่อยู่ในกล่องข้อความ และเปิดเข้าหน้าโพสต์',
  'group.kindNote': 'ทั้งสองอย่างใช้ได้ไม่ว่าเลือกแบบไหน ตัวเลือกนี้กำหนดว่ากลุ่มไปอยู่ที่ไหนและเปิดเข้าอะไร ผู้ดูแลเปลี่ยนทีหลังได้',
  'group.kindMakeChat': 'เปลี่ยนเป็นกลุ่มแชท',
  'group.kindMakePosts': 'เปลี่ยนเป็นกลุ่มโพสต์',
  'group.kindSwitchWhy': 'เปลี่ยนที่อยู่ของกลุ่มและหน้าที่เปิดเข้า ไม่มีอะไรหายไม่ว่าทางไหน ทั้งแชทและฟีดยังใช้ได้ทั้งคู่',
  'group.editCover': 'แก้ไขรูปหน้าปก',
  'group.joined': 'เข้าร่วมแล้ว',
  'group.searchInside': 'ค้นหาในกลุ่ม',
  'group.searchInsideWhy': 'ยังไม่มีการค้นหาภายในกลุ่ม ช่องที่กรองเฉพาะโพสต์ที่โหลดมาแล้วไม่ใช่การค้นหา จึงรอจนกว่าจะมีตัวที่ถามไปที่เซิร์ฟเวอร์',
  'group.andOthers': 'และอีก {count} คนเป็นสมาชิก',
  'group.tabHighlights': 'เด่น / ไฮไลต์',
  'group.tabHighlightsWhy': 'ยังไม่มีอะไรทำเครื่องหมายว่าโพสต์ไหนเป็นไฮไลต์ และไม่มีอะไรจัดอันดับโพสต์',
  'group.tabEventsWhy': 'กิจกรรมต้องมีวันเวลา รายชื่อผู้เข้าร่วม และการแจ้งเตือน ทำได้แค่หนึ่งในสามแย่กว่าไม่ทำ',
  'group.tabFiles': 'ไฟล์ & แหล่งเรียนรู้',
  'group.tabFilesWhy': 'การอัปโหลดที่นี่เป็นรูปภาพ เพราะ pipeline คือ Sharp ซึ่งจัดการเอกสารไม่ได้',
  'group.organisersTitle': 'ผู้ดูแลกลุ่ม',
  'group.seeAll': 'ดูทุกคน',
  'group.messageOrganiser': 'เปิดโปรไฟล์',
  'group.messageOrganiserWhy': 'การส่งข้อความใช้ได้ระหว่างเพื่อน ปุ่มนี้จึงเปิดโปรไฟล์ซึ่งเป็นที่เริ่มส่งคำขอเป็นเพื่อน',
  'group.createdTitle': 'สร้างเมื่อ',
  'group.rulesBy': 'โดยผู้ดูแล',
  'groups.title': 'กลุ่ม',
  'groups.lede': 'กลุ่มที่คุณอยู่ และกลุ่มที่คุณหาเจอ',
  'groups.create': 'สร้างกลุ่ม',
  'groups.discoverTitle': 'ค้นหากลุ่ม',
  'groups.searchPlaceholder': 'ค้นหากลุ่มจากชื่อ…',
  'groups.searchHint': 'ค้นจากคำขึ้นต้นของชื่อกลุ่ม',
  'groups.allCategories': 'ทั้งหมด',
  'groups.posts': '{count} โพสต์',
  'groups.join': 'เข้าร่วม',
  'groups.askToJoin': 'ขอเข้าร่วม',
  'groups.asked': 'รออนุมัติ',
  'groups.open': 'เปิด',
  'groups.more': 'ดูเพิ่ม',
  'groups.yoursTitle': 'กลุ่มของคุณ',
  'groups.yoursEmpty': 'คุณยังไม่ได้อยู่กลุ่มไหน ลองหาจากด้านล่าง หรือสร้างกลุ่มของตัวเอง',
  'groups.noneTitle': 'ไม่มีกลุ่มที่ตรงกับที่เลือก',
  'groups.noneBody': 'ไม่มีกลุ่มที่หาเจอตรงกับชื่อหรือหมวดหมู่ที่เลือกไว้ กลุ่มที่ตั้งเป็นซ่อนจะเข้าได้ด้วยคำเชิญเท่านั้นและไม่ปรากฏที่นี่',
  'groups.failedTitle': 'โหลดรายชื่อกลุ่มไม่สำเร็จ',
  'groups.failedBody': 'มีอะไรผิดพลาดระหว่างดึงข้อมูล ลองอีกครั้งในอีกสักครู่',
  'groups.suggestedTitle': 'กลุ่มที่คุณอาจสนใจ',
  'groups.suggestedWhy': 'ยังไม่มีอะไรใช้แนะนำกลุ่มได้ สัญญาณเดียวที่มีคือหมวดหมู่ซึ่งกรองเองได้อยู่แล้ว การเรียกตัวกรองว่าคำแนะนำคือการอ้างเหตุผลที่ไม่มีจริง',
  'group.joinPolicy': 'การเข้ากลุ่ม',
  'group.joinApproval': 'อนุมัติทีละคน',
  'group.joinApprovalHelp': 'ผู้ดูแลเป็นคนตัดสินใจทั้งคนที่ขอเข้าเอง และคนที่สมาชิกชวนมา',
  'group.joinOpen': 'ใครก็เข้าได้เลย',
  'group.joinOpenHelp': 'คนที่หากลุ่มเจอเข้าได้ทันที และคนที่สมาชิกชวนก็เข้าได้เลย',
  'group.joinPolicyNote': 'สมาชิกชวนเพื่อนได้ทั้งสองแบบ ตัวเลือกนี้กำหนดว่าคนที่ถูกชวนเข้าได้เลยหรือต้องรอผู้ดูแล',
  'group.inviteOne': 'ชวน',
  'group.invited': 'ชวนแล้ว',
  'group.inviteNobody': 'เพื่อนของคุณอยู่ในกลุ่มหรือรออนุมัติอยู่แล้วทั้งหมด',
  'group.inviteOpenNote': 'คนที่คุณชวนจะเข้ากลุ่มได้ทันที',
  'group.inviteApprovalNote': 'คนที่คุณชวนต้องรอผู้ดูแลอนุมัติ ยกเว้นคุณเป็นผู้ดูแลเอง',
  'group.invitedBy': '{name} ชวนเข้ากลุ่ม',
  'group.askedToJoin': 'ขอเข้ากลุ่มเอง',
  'group.waitingTitle': 'รออนุมัติเข้ากลุ่ม',
  'group.approve': 'อนุมัติ',
  'group.decline': 'ปฏิเสธ',
  'group.roleOwner': 'เจ้าของกลุ่ม',
  'group.roleAdmin': 'ผู้ดูแล',
  'group.roleMember': 'สมาชิก',
  'group.you': 'คุณ',
  'group.name': 'ชื่อกลุ่ม',
  'group.namePlaceholder': 'นัดเจอกันเสาร์นี้',
  'group.searchConnections': 'ค้นหาจากเพื่อนของคุณ',
  'group.needConnections': 'ต้องมีเพื่อนก่อนถึงจะสร้างกลุ่มได้',
  'group.noMatches': 'ไม่พบใครที่ตรงกับที่ค้นหา',
  'group.selectedCount': 'เลือกแล้ว {selected} คน · ได้สูงสุด {max} คน',
  'group.creating': 'กำลังสร้าง…',
  'group.leave': 'ออกจากกลุ่ม',
  'group.leaveConfirm': 'ออกจากกลุ่ม “{name}” ใช่ไหม',
  'group.members.one': '{count} คน',
  'group.members.other': '{count} คน',
  'group.someoneTyping': '{name} กำลังพิมพ์…',
  'group.peopleTyping': '{count} คนกำลังพิมพ์…',

  'group.feed': 'โพสต์',
  'group.chat': 'แชท',
  'group.writePost': 'แบ่งปันอะไรกับกลุ่ม',
  'group.noPosts': 'ยังไม่มีโพสต์',
  'group.noPostsHelp': 'เป็นคนแรกที่โพสต์ที่นี่',
  'group.membersOnly': 'เฉพาะสมาชิกเท่านั้นที่เห็นสิ่งที่โพสต์ที่นี่',

  /* Discover */
  'discover.title': 'ค้นหาเพื่อน',
  'discover.searchPlaceholder': 'ค้นหาจากชื่อหรือ @ชื่อผู้ใช้',
  'discover.filterCountry': 'ประเทศ',
  'discover.filterLanguage': 'ภาษา',
  'discover.filterInterest': 'ความสนใจ',
  'discover.anyCountry': 'ทุกประเทศ',
  'discover.anyLanguage': 'ทุกภาษา',
  'discover.anyInterest': 'ทุกความสนใจ',
  'discover.noResults': 'ไม่พบใครเลย',
  'discover.noResultsHelp': 'ลองเปลี่ยนประเทศ ภาษา หรือความสนใจดู',
  'discover.loadMore': 'ดูเพิ่ม',
  'discover.searching': 'กำลังค้นหา…',

  /* Friends and connections */
  'friends.title': 'เพื่อน',
  'friends.lede': 'รายชื่อเพื่อนของคุณ คำขอที่รออยู่ และคนที่น่าทำความรู้จัก',
  'friends.onlineNow': 'ออนไลน์ {count} คน',
  'friends.tabAll': 'เพื่อนทั้งหมด',
  'friends.tabIncoming': 'คำขอเป็นเพื่อน',
  'friends.tabOutgoing': 'คำขอที่ส่งแล้ว',
  'friends.tabRecent': 'เพื่อนที่เพิ่งเพิ่ม',
  'friends.tabClose': 'เพื่อนสนิท',
  'friends.tabBirthdays': 'วันเกิด',
  'friends.searchPlaceholder': 'ค้นหาตามชื่อหรือชื่อผู้ใช้…',
  'friends.filterCountry': 'ทุกประเทศ',
  'friends.filterSchool': 'สถาบันการศึกษา',
  'friends.filterWork': 'ที่ทำงาน',
  'friends.sortBy': 'เรียงลำดับ',
  'friends.sortRecent': 'เพิ่มล่าสุด',
  'friends.sortName': 'ตามตัวอักษร',
  'friends.sortMutual': 'เพื่อนร่วมกันมากที่สุด',
  'friends.viewGrid': 'ตาราง',
  'friends.viewList': 'รายการ',
  'friends.findNew': 'ค้นหาเพื่อนใหม่',
  'friends.showing': 'แสดง {shown} จาก {total}',
  'friends.noMatch': 'ไม่พบใครที่ตรงกับที่ค้นหา',
  'friends.noMatchHelp': 'ลองเปลี่ยนคำค้น หรือล้างตัวกรองดู',
  'friends.noIncoming': 'ไม่มีคำขอรออยู่',
  'friends.noOutgoing': 'คุณยังไม่ได้ส่งคำขอถึงใคร',
  'friends.suggestions': 'คนที่คุณอาจรู้จัก',
  'friends.seeAll': 'ดูทั้งหมด',
  'friends.closeFriend': 'เพื่อนสนิท',
  'friends.mute': 'ปิดการแจ้งโพสต์',
  'friends.viewProfile': 'ดูโปรไฟล์',
  'friends.connected': 'เพื่อน ({count})',
  'friends.requests': 'คำขอ ({count})',
  'friends.connect': 'ขอเป็นเพื่อน',
  'friends.requestSent': 'ส่งคำขอแล้ว',
  'friends.accept': 'ตอบรับ',
  'friends.reject': 'ปฏิเสธ',
  'friends.cancel': 'ยกเลิกคำขอ',
  'friends.remove': 'ลบออกจากเพื่อน',
  'friends.message': 'ส่งข้อความ',
  'friends.follow': 'ติดตาม',
  'friends.unfollow': 'กำลังติดตาม',
  'friends.unfollowAction': 'เลิกติดตาม',
  'friends.following.busy': 'กำลังติดตาม…',
  'friends.unfollowing.busy': 'กำลังเลิกติดตาม…',
  'friends.block': 'บล็อก',
  'friends.unblock': 'เลิกบล็อก',
  'friends.report': 'รายงาน',
  'friends.none': 'ยังไม่มีเพื่อน',
  'friends.noneHelp': 'ลองหาคนที่ใช้ภาษาเดียวกันหรือสนใจเรื่องเดียวกัน',
  'friends.noRequests': 'ไม่มีคำขอที่รออยู่',
  'friends.since': 'เป็นเพื่อนกันเมื่อ {when}',

  /* Profile */
  'profile.editProfile': 'แก้ไขโปรไฟล์',
  'profile.followers.one': 'ผู้ติดตาม {count} คน',
  'profile.friendCount.one': 'เพื่อน {count} คน',
  'profile.friendCount.other': 'เพื่อน {count} คน',
  'profile.followers.other': 'ผู้ติดตาม {count} คน',
  'profile.following': 'กำลังติดตาม {count} คน',
  'profile.interests': 'ความสนใจ',
  'profile.speaks': 'พูดได้',
  'profile.reads': 'อ่านเป็น',
  'profile.birthday': 'เกิดวันที่ {when}',
  'profile.joined': 'เข้าร่วมเมื่อ {when}',
  'profile.notFound': 'ไม่พบโปรไฟล์',
  'profile.notFoundHelp': 'บัญชีนี้ไม่มีอยู่ หรือคุณไม่มีสิทธิ์ดู',
  'profile.loadFailed': 'โหลดโปรไฟล์นี้ไม่สำเร็จ',
  'profile.ownNotice': 'นี่คือหน้าที่คนอื่นเห็นโปรไฟล์ของคุณ',
  'profile.sending': 'กำลังส่ง…',
  'profile.requestSent': 'ส่งคำขอแล้ว',
  'profile.accepting': 'กำลังตอบรับ…',
  'profile.acceptRequest': 'ตอบรับคำขอ',
  'profile.opening': 'กำลังเปิด…',
  'profile.friends': 'เพื่อน',
  'profile.about': 'เกี่ยวกับ',
  'profile.links': 'ลิงก์',
  'profile.intro': 'แนะนำตัว',
  'profile.livesIn': 'อาศัยอยู่ที่ {place}',
  'profile.readsIn': 'อ่านเป็น{language}',
  'profile.localTime': 'เวลาท้องถิ่น {time}',
  'profile.followsYouShort': 'ติดตามคุณอยู่',
  'profile.tabPosts': 'โพสต์ทั้งหมด',
  'profile.tabAbout': 'เกี่ยวกับ',
  'profile.tabFriends': 'เพื่อน',
  'profile.tabPhotos': 'รูปภาพและสื่อ',
  'profile.tabPages': 'เพจที่ติดตาม',
  'profile.tabSpaces': 'ห้องเสียง',
  'profile.filterAll': 'โพสต์ทั้งหมด',
  'profile.filterPinned': 'โพสต์ที่ปักหมุด',
  'profile.filterMedia': 'สื่อและวิดีโอ',
  'profile.work': 'ที่ทำงาน',
  'profile.education': 'การศึกษา',
  'profile.followersTitle': 'ผู้ติดตาม',
  'profile.historyTitle': 'ที่ทำงานและการศึกษา',
  'profile.workTitle': 'ที่ทำงาน',
  'profile.educationTitle': 'การศึกษา',
  'profile.present': 'ปัจจุบัน',
  'profile.friendsTitle': 'เพื่อน',
  'profile.friendsEmpty': 'ยังไม่มีเพื่อนที่จะแสดง',
  'profile.photosEmpty': 'ยังไม่มีรูปที่โพสต์ไว้',
  'profile.pagesEmpty': 'ยังไม่ได้ติดตามเพจไหน',
  'profile.followersEmpty': 'ยังไม่มีใครติดตามบัญชีนี้',
  'profile.seeAll': 'ดูทั้งหมด',
  'profile.copyLink': 'คัดลอกลิงก์โปรไฟล์',
  'profile.linkCopied': 'คัดลอกลิงก์แล้ว',
  'profile.block': 'บล็อกผู้ใช้นี้',
  'profile.unfriend': 'เลิกเป็นเพื่อน',
  'profile.muteposts': 'ปิดการแจ้งโพสต์',
  'profile.restrict': 'จำกัดการมองเห็น',
  'profile.audioCall': 'โทรด้วยเสียง',
  'profile.moreOptions': 'ตัวเลือกเพิ่มเติม',
  'profile.friendsSince': 'เป็นเพื่อนแล้ว',
  'profile.connectFailed': 'ส่งคำขอไม่สำเร็จ',

  /* Notifications */
  'notifications.title': 'การแจ้งเตือน',
  'notifications.lede': 'ความเคลื่อนไหว การตอบกลับ และทุกอย่างที่เกี่ยวกับคุณ',
  'notifications.unreadCount': 'ยังไม่ได้อ่าน {count} รายการ',
  'notifications.allRead': 'อ่านแล้วทั้งหมด',
  'notifications.settings': 'ตั้งค่าการแจ้งเตือน',
  'notifications.someone': 'บางคน',
  'notifications.filterAll': 'ทั้งหมด',
  'notifications.filterUnread': 'ยังไม่ได้อ่าน',
  'notifications.filterPosts': 'โพสต์และความคิดเห็น',
  'notifications.filterFriends': 'เพื่อนและคำขอ',
  'notifications.filterGroups': 'กลุ่มและเพจ',
  'notifications.filterEvents': 'กิจกรรมและวันเกิด',
  'notifications.when.today': 'วันนี้',
  'notifications.when.yesterday': 'เมื่อวานนี้',
  'notifications.when.week': 'สัปดาห์นี้',
  'notifications.when.earlier': 'ก่อนหน้านี้',
  'notifications.sectionCount': '{count} รายการ',
  'notifications.caughtUp': 'คุณดูครบแล้ว',
  'notifications.caughtUpHelp': 'มีอะไรใหม่จะขึ้นที่นี่ทันที',
  'notifications.pendingTitle': 'คำขอที่รอดำเนินการ',
  'notifications.pendingNone': 'ไม่มีคำขอรออยู่',
  'notifications.eventsTitle': 'กิจกรรมเร็วๆ นี้',
  'notifications.eventsHelp': 'ยังไม่มีอะไรสร้างกิจกรรมได้',
  'notifications.liveOn': 'เชื่อมต่ออยู่ — แจ้งเตือนใหม่มาแบบสด',
  'notifications.liveOff': 'ไม่ได้เชื่อมต่อ — รีเฟรชเพื่อดูของใหม่',
  'notifications.unread': 'ยังไม่ได้อ่าน',
  'notifications.markAllRead': 'อ่านทั้งหมดแล้ว',
  'notifications.none': 'ยังไม่มีอะไร',
  'notifications.noneHelp': 'คำขอ การตอบรับ และข้อความจะมาแสดงตรงนี้',
  'notifications.friendRequest': '{name} ขอเป็นเพื่อน',
  'notifications.friendAccepted': '{name} ตอบรับคำขอของคุณแล้ว',
  'notifications.newMessage': '{name} ส่งข้อความถึงคุณ',
  'notifications.groupPost': '{name} โพสต์ในกลุ่ม',
  'notifications.groupMessage': 'ข้อความใหม่ใน {name}',
  'notifications.profilePost': '{name} เขียนบนโปรไฟล์ของคุณ',
  'notifications.postRepost': '{name} รีโพสต์โพสต์ของคุณ',
  'notifications.storyMention': '{name} แท็กคุณในสตอรี่',
  'settings.notifyStoryMention': 'เมื่อมีคนแท็กฉันในสตอรี่',
  'notifications.aGroup': 'กลุ่มหนึ่ง',
  'notifications.loadMore': 'ดูเพิ่ม',

  /* Settings */
  'settings.title': 'ตั้งค่า',
  'settings.breadcrumb': 'บัญชีและความเป็นส่วนตัว',
  'settings.lede': 'ข้อมูลของคุณ ใครเห็นได้บ้าง คุณเข้าสู่ระบบอย่างไร และ TalkVibe ส่งอะไรถึงคุณ',
  'settings.searchPlaceholder': 'ค้นหาการตั้งค่าทั้งหมด…',
  'settings.welcome': 'ยินดีต้อนรับสู่ TalkVibe เพิ่มความสนใจสักหน่อยเพื่อให้คนอื่นหาคุณเจอ',
  'settings.catProfile': 'ข้อมูลบัญชี และโปรไฟล์',
  'settings.catSecurity': 'รหัสผ่าน และความปลอดภัย',
  'settings.catPrivacy': 'ความเป็นส่วนตัว และการมองเห็น',
  'settings.catNotifications': 'การแจ้งเตือน',
  'settings.catBlocked': 'การบล็อก และคำที่ซ่อน',
  'settings.catAudio': 'เสียง และการสตรีม',
  'settings.catLanguage': 'ภาษาและภูมิภาค',
  'settings.catDevices': 'การเข้าสู่ระบบ และอุปกรณ์',
  'settings.accountStatus': 'สถานะบัญชี และการลบบัญชี',
  'settings.publicTitle': 'ข้อมูลสาธารณะ',
  'settings.publicLede': 'ข้อมูลเหล่านี้จะปรากฏบนโปรไฟล์ และเป็นสิ่งที่คนบน TalkVibe เห็นได้',
  'settings.viewProfile': 'ดูโปรไฟล์ของคุณ',
  'settings.photos': 'ภาพหน้าปกและรูปโปรไฟล์',
  'settings.photoHint': 'JPEG, PNG, WebP หรือ GIF ไม่เกิน {max} MB',
  'settings.changeAvatar': 'เปลี่ยนรูป',
  'settings.changeCover': 'เปลี่ยนภาพปก',
  'settings.uploading': 'กำลังอัปโหลด…',
  'settings.usernameFixed': 'โปรไฟล์ของคุณอยู่ที่ {url} และยังเปลี่ยนชื่อผู้ใช้ไม่ได้',
  'settings.linkLabel': 'ชื่อลิงก์',
  'settings.addLink': 'เพิ่มลิงก์',
  'settings.removeLink': 'เอาลิงก์นี้ออก',
  'settings.interestsTitle': 'ความสนใจและภาษา',
  'settings.interestsLede': 'เลือกได้ถึง {max} อย่าง นี่คือสิ่งที่หน้าค้นหาเพื่อนใช้จับคู่ ใส่ไว้จะดีกว่า',
  'settings.proficiency': 'ระดับที่พูดได้',
  'settings.addLanguage': 'เพิ่มภาษาที่คุณพูดได้',
  'settings.removeLanguage': 'เอาภาษานี้ออก',
  'settings.contactTitle': 'ข้อมูลติดต่อและบัญชี',
  'settings.contactLede': 'เป็นความลับ ไม่แสดงบนโปรไฟล์ของคุณ',
  'settings.verified': 'ยืนยันแล้ว',
  'settings.notVerified': 'ยังไม่ยืนยัน',
  'settings.emailHelp': 'ที่อยู่สำหรับส่งลิงก์เข้าสู่ระบบและลิงก์ตั้งรหัสผ่านใหม่',
  'settings.sendVerification': 'ส่งลิงก์',
  'settings.sending': 'กำลังส่ง…',
  'settings.verificationSent': 'ส่งแล้ว ลองเช็คกล่องจดหมาย',
  'settings.changeEmail': 'แก้ไขอีเมล',
  'settings.phone': 'เบอร์โทรศัพท์',
  'settings.phoneHelp': 'สำหรับเข้าสู่ระบบและรับรหัสทาง SMS',
  'settings.birthdateHelp': 'กรอกไว้ตอนสมัคร และแก้ที่นี่ไม่ได้',
  'settings.birthdateAudience': 'คนอื่นเห็นแค่วันและเดือน ไม่เห็นปีเกิด',
  'settings.securityTitle': 'การเข้าสู่ระบบอย่างปลอดภัย',
  'settings.securityLede': 'สิ่งที่กั้นระหว่างบัญชีของคุณกับคนที่ไม่ใช่คุณ',
  'settings.twoFactor': 'การยืนยันตัวตนสองชั้น',
  'settings.twoFactorHelp': 'ใส่รหัสอีกชั้นตอนเข้าสู่ระบบ จากแอป authenticator',
  'settings.passwordHelp': 'อย่างน้อย {min} ตัวอักษร การเปลี่ยนจะออกจากระบบทุกอุปกรณ์อื่น',
  'settings.currentPassword': 'รหัสผ่านปัจจุบัน',
  'settings.newPassword': 'รหัสผ่านใหม่',
  'settings.passwordChanged': 'เปลี่ยนรหัสผ่านแล้ว อุปกรณ์อื่นถูกออกจากระบบทั้งหมด',
  'settings.privacyTitle': 'ใครเห็นคุณได้บ้าง',
  'settings.privacyLede': 'ใครหาโปรไฟล์คุณเจอ ส่งข้อความถึงคุณได้ และเห็นว่าคุณออนไลน์',
  'settings.optSearchEngines': 'ให้เครื่องมือค้นหาแสดงโปรไฟล์ TalkVibe ของคุณ',
  'settings.optSearchEnginesHelp': 'Google และ Bing จะแสดงโปรไฟล์สาธารณะของคุณหรือไม่',
  'settings.optOnlineStatus': 'แสดงสถานะออนไลน์',
  'settings.optOnlineStatusHelp': 'จุดเขียวบนรูปโปรไฟล์ ให้เพื่อนและผู้ติดตามเห็น',
  'settings.optWhoCanMessage': 'ใครส่งข้อความหาคุณได้',
  'settings.optWhoCanMessageHelp': 'ตอนนี้กติกาการเชื่อมต่อเป็นตัวกำหนด แชทต้องเป็นเพื่อนกันก่อน',
  'settings.optReadReceipts': 'ส่งสถานะการอ่านในแชท',
  'settings.optReadReceiptsHelp': 'อีกฝ่ายจะรู้หรือไม่ว่าคุณเปิดอ่านข้อความแล้ว',
  'settings.blockedTitle': 'คนที่คุณบล็อกไว้',
  'settings.blockedLede': 'เขาส่งข้อความหาคุณไม่ได้ ดูโปรไฟล์ไม่ได้ และหาคุณไม่เจอ โดยที่เขาไม่รู้ตัว',
  'settings.blockedNone': 'คุณยังไม่ได้บล็อกใคร',
  'settings.unblock': 'เลิกบล็อก',
  'settings.mutedWords': 'คำที่ซ่อน',
  'settings.mutedWordsHelp': 'ซ่อนโพสต์ที่มีคำที่คุณไม่อยากอ่าน',
  'settings.languageTitle': 'ภาษาและภูมิภาค',
  'settings.languageLede': 'ภาษาที่คุณอ่าน และเวลาที่คนอื่นเห็นข้างชื่อคุณ',
  'settings.timezoneNone': 'ยังไม่ได้ตั้ง',
  'settings.useDetected': 'ใช้ {zone} ที่ตรวจพบจากเครื่องนี้',
  'settings.sessionsLede': 'ทุกอุปกรณ์ที่เข้าสู่ระบบบัญชีนี้ ออกจากระบบอันที่คุณไม่รู้จัก',
  'settings.unsaved': 'คุณมีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก',
  'settings.unsavedHelp': 'ข้อมูลจะยังไม่ถูกส่งไป TalkVibe จนกว่าคุณจะกดบันทึก',
  'settings.visibilityHelp.public': 'ทุกคนที่ล็อกอิน TalkVibe เห็นโปรไฟล์ของคุณได้',
  'settings.visibilityHelp.friends': 'เฉพาะคนที่เชื่อมต่อกับคุณแล้วเท่านั้น',
  'settings.visibilityHelp.private': 'ไม่มีใครนอกจากคุณ และคุณจะไม่ปรากฏในหน้าค้นหาเพื่อน',
  'settings.proficiency.learning': 'กำลังเรียน',
  'settings.proficiency.conversational': 'พอสื่อสารได้',
  'settings.proficiency.fluent': 'คล่อง',
  'settings.proficiency.native': 'ภาษาแม่',
  'settings.profile': 'โปรไฟล์',
  'settings.account': 'บัญชี',
  'settings.privacy': 'ความเป็นส่วนตัว',
  'settings.security': 'ความปลอดภัย',
  'settings.sessions': 'อุปกรณ์ที่เข้าสู่ระบบอยู่',
  'settings.save': 'บันทึก',
  'settings.saving': 'กำลังบันทึก…',
  'settings.saved': 'บันทึกแล้ว',
  'settings.bio': 'เกี่ยวกับคุณ',
  'settings.visibility': 'ใครเห็นโปรไฟล์ของคุณได้บ้าง',
  'settings.visibilityPublic': 'ทุกคน',
  'settings.visibilityFriends': 'เฉพาะเพื่อน',
  'settings.visibilityPrivate': 'เฉพาะฉัน',
  'settings.timezone': 'เขตเวลา',
  'settings.spokenLanguages': 'ภาษาที่คุณพูดได้',
  'settings.work': 'ที่ทำงาน',
  'settings.education': 'การศึกษา',
  'settings.employer': 'ที่ทำงาน',
  'settings.jobTitle': 'ตำแหน่ง (ไม่ใส่ก็ได้)',
  'settings.school': 'โรงเรียนหรือมหาวิทยาลัย',
  'settings.fieldOfStudy': 'สาขาที่เรียน (ไม่ใส่ก็ได้)',
  'settings.fromYear': 'ตั้งแต่ปี',
  'settings.toYear': 'ถึงปี',
  'settings.addEntry': 'เพิ่ม',
  'settings.removeEntry': 'ลบรายการนี้',
  'settings.socialLinks': 'ลิงก์',
  'settings.changePassword': 'เปลี่ยนรหัสผ่าน',
  'settings.signOutEverywhere': 'ออกจากระบบทุกอุปกรณ์',
  'settings.revokeSession': 'ออกจากระบบ',
  'settings.thisDevice': 'อุปกรณ์นี้',
  'settings.lastSeen': 'ใช้ล่าสุดเมื่อ {when}',

  /* Reporting and moderation */
  'report.title': 'รายงาน {name}',
  'report.whatIsWrong': 'มีอะไรไม่เหมาะสม',
  'report.anythingElse': 'มีอะไรอยากบอกเพิ่มไหม',
  'report.optional': 'ไม่บังคับ',
  'report.send': 'ส่งรายงาน',
  'report.sending': 'กำลังส่ง…',
  'report.thanks': 'ขอบคุณ — ส่งถึงผู้ดูแลแล้ว',
  'report.private': 'เราจะไม่บอก {name} ว่าใครเป็นคนรายงาน',
  'report.notice': 'ผู้ดูแลจะเป็นคนอ่านรายงาน {name} จะไม่รู้ว่าคุณรายงาน และไม่เห็นสิ่งที่คุณเขียน',
  'report.reason.spam': 'สแปมหรือโฆษณาที่ไม่ต้องการ',
  'report.reason.harassment': 'คุกคามหรือกลั่นแกล้ง',
  'report.reason.hate': 'สร้างความเกลียดชัง',
  'report.reason.sexual': 'เนื้อหาทางเพศ',
  'report.reason.violence': 'ความรุนแรงหรือข่มขู่',
  'report.reason.impersonation': 'แอบอ้างเป็นคนอื่น',
  'report.reason.scam': 'หลอกลวงหรือฉ้อโกง',
  'report.reason.other': 'เรื่องอื่น',
  'moderation.title': 'ดูแลระบบ',
  'moderation.waiting': 'รออยู่ {count} รายการ',
  'moderation.reviewed': 'ตรวจแล้ว',
  'moderation.open': 'รอตรวจ',
  'moderation.actioned': 'ดำเนินการแล้ว',
  'moderation.dismissed': 'ยกคำร้อง',
  'moderation.nothing': 'ไม่มีรายการ',
  'moderation.nothingOpen': 'ไม่มีรายงานที่รออยู่',
  'moderation.nothingReviewed': 'ยังไม่มีรายการที่ตรวจแล้ว',
  'moderation.dismiss': 'ยกคำร้อง',
  'moderation.removeMessage': 'ลบข้อความ',
  'moderation.suspend': 'ระงับบัญชี',
  'moderation.notePlaceholder': 'บันทึกเหตุผล (ไม่บังคับ)',
  'moderation.reportedAgo': 'รายงานเมื่อ {when}',
  'moderation.multipleReports': 'มี {count} คนรายงานคนนี้',
  'moderation.theySaid': 'ผู้รายงานเขียนว่า',
  'moderation.noEvidence': 'ไม่มีเนื้อหาที่บันทึกไว้',

  /* Pages */
  'pages.title': 'เพจ',
  'pages.create': 'สร้างเพจ',
  'pages.newTitle': 'สร้างเพจใหม่',
  'pages.newLede': 'ตั้งชื่อและเลือกหมวดหมู่เพื่อให้คนอื่นค้นเจอ ทุกอย่างในหน้านี้แก้ทีหลังได้ ยกเว้นที่อยู่เพจ',
  'pages.identity': 'เพจนี้คืออะไร',
  'pages.nameHelp': 'ชื่อธุรกิจ แบรนด์ ชุมชน หรือชื่อที่บอกได้ว่าเพจนี้มีไว้ทำอะไร',
  'pages.addressHelp': 'นี่คือ URL ของเพจ เปลี่ยนทีหลังไม่ได้',
  'pages.categoryHelp': 'เลือกได้หนึ่งหมวด เป็นทางที่คนเลือกดูตามหมวดจะเจอคุณ',
  'pages.taglineHelp': 'หนึ่งบรรทัด แสดงใต้ชื่อเพจ',
  'pages.descriptionHelp': 'รายละเอียดยาว แสดงในกล่องเกี่ยวกับเพจ',
  'pages.images': 'รูปภาพ',
  'pages.avatar': 'รูปโปรไฟล์เพจ',
  'pages.coverImage': 'รูปหน้าปกเพจ',
  'pages.changeImage': 'เปลี่ยน',
  'pages.removeImage': 'เอารูปออก',
  'pages.imageFailed': 'สร้างเพจแล้ว แต่อัปโหลด {what} ไม่สำเร็จ ลองอีกครั้งจากหน้าจัดการเพจ',
  'pages.website': 'เว็บไซต์',
  'pages.websitePlaceholder': 'example.com',
  'pages.websiteHelp': 'ถ้าไม่ใส่ https:// ให้ เราจะเติมเอง',
  'pages.contactEmail': 'อีเมลติดต่อ',
  'pages.contactEmailHelp': 'จะแสดงบนหน้าเพจ ไม่ใช่อีเมลที่คุณใช้เข้าสู่ระบบ',
  'pages.contactPhone': 'เบอร์โทรติดต่อ',
  'pages.avatarLabel': 'รูปโปรไฟล์เพจ',
  'pages.avatarHint': 'จัตุรัส ประมาณ 320 × 320',
  'pages.coverLabel': 'รูปหน้าปกเพจ',
  'pages.coverHint': 'แนวนอน ประมาณ 1200 × 450',
  'pages.addImage': 'เพิ่มรูป',
  'pages.contact': 'ข้อมูลติดต่อและเว็บไซต์',
  'pages.optional': 'ไม่บังคับ',
  'pages.termsNotice': 'การสร้างเพจถือว่าคุณยอมรับ{terms}',
  'pages.creating': 'กำลังสร้าง…',
  'pages.tipsTitle': 'เคล็ดลับการตั้งชื่อ',
  'pages.tipsBody': 'เลี่ยงคำว่า "เพจอย่างเป็นทางการ" และการพิมพ์ตัวใหญ่ทั้งหมด ชื่อที่ดีบอกได้ทันทีว่าคนกดติดตามแล้วจะได้อะไร',
  'pages.preview': 'ตัวอย่างแบบเรียลไทม์',
  'pages.previewNote': 'นี่คือหน้าตาเพจของคุณในสายตาคนอื่น เมื่อสร้างเสร็จจะเปลี่ยนรูปและโพสต์ได้ทันที',
  'pages.previewDesktop': 'เดสก์ท็อป',
  'pages.previewMobile': 'มือถือ',
  'pages.previewNameEmpty': 'ชื่อเพจของคุณ',
  'pages.previewBioEmpty': 'คำอธิบายเกี่ยวกับเพจนี้จะปรากฏตรงนี้',
  'pages.previewCover': 'พื้นที่รูปหน้าปกเพจ',
  'pages.previewFollow': 'ติดตาม',
  'pages.previewMessage': 'ส่งข้อความ',
  'pages.previewFirstPost': 'เขียนโพสต์แรกของคุณในฐานะ {name}…',
  'pages.previewEmptyTitle': 'พร้อมรับผู้ติดตามคนแรก',
  'pages.previewEmptyBody': 'เมื่อเพจถูกสร้างแล้ว โพสต์และรูปที่คุณเผยแพร่จะมาแสดงตรงนี้',
  'pages.steps': 'ขั้นตอน',
  'pages.name': 'ชื่อเพจ',
  'pages.address': 'ที่อยู่เพจ',
  'pages.tagline': 'คำอธิบายสั้นๆ หนึ่งบรรทัด',
  'pages.description': 'รายละเอียด',
  'pages.category': 'หมวดหมู่',
  'pages.visibility': 'การมองเห็น',
  'pages.noCategory': 'ไม่ระบุหมวดหมู่',

  'pageManage.title': 'จัดการเพจ',
  'pageManage.edit': 'แก้ไข',
  'pageManage.insights': 'ข้อมูลเชิงลึก',
  'pageManage.ads': 'โฆษณา',
  'pageManage.save': 'บันทึกการแก้ไข',
  'pageManage.saved': 'บันทึกแล้ว',
  'pageManage.noChanges': 'ยังไม่มีอะไรให้บันทึก',
  'pageManage.notYours': 'เพจนี้ไม่ใช่เพจที่คุณจัดการได้',
  'pageManage.viewInstead': 'ไปดูหน้าเพจแทน',
  'pageManage.imagesSoon': 'ยังอัปโหลดรูปโปรไฟล์และแบนเนอร์ของเพจไม่ได้',
  'pageManage.followers': 'ผู้ติดตาม',
  'pageManage.posts': 'โพสต์',
  'pageManage.likes': 'ถูกใจ',
  'pageManage.comments': 'ความคิดเห็น',
  'pageManage.recentPosts': 'โพสต์ใน 30 วัน',
  'pageManage.perPost': 'ปฏิกิริยาต่อโพสต์',
  'pageManage.noViews':
    'ที่นี่ไม่มียอดดู ไม่มีการเข้าถึง และไม่มีการเปลี่ยนแปลงรายสัปดาห์ เพราะ TalkVibe '
    + 'ไม่ได้บันทึกการดูอะไรเลย และไม่ได้เก็บสแนปช็อตจำนวนผู้ติดตามรายวัน — ตัวเลขเหล่านั้นจึงเป็น '
    + 'สิ่งที่เราผลิตไม่ได้ ไม่ใช่สิ่งที่เราลืมใส่ การเอาค่าประมาณมาใส่แทนแย่กว่าการเว้นว่าง',
  'pageManage.topPosts': 'ได้ปฏิกิริยามากที่สุด',
  'pageManage.noTopPosts': 'ยังไม่มีโพสต์ไหนได้ถูกใจหรือความคิดเห็น',
  'pageManage.noWords': 'โพสต์ที่ไม่มีข้อความ',
  'pages.public': 'สาธารณะ',
  'pages.unlisted': 'ไม่แสดงในรายการ — เข้าได้ทางลิงก์เท่านั้น',
  'pages.follow': 'ติดตาม',
  'pages.unfollow': 'กำลังติดตาม',
  'pages.followers.one': 'ผู้ติดตาม {count} คน',
  'pages.followers.other': 'ผู้ติดตาม {count} คน',
  'pages.posts.one': '{count} โพสต์',
  'pages.posts.other': '{count} โพสต์',
  'pages.writePost': 'เขียนโพสต์',
  'pages.publish': 'เผยแพร่',
  'pages.noPosts': 'ยังไม่มีโพสต์',
  'pages.mine': 'เพจของคุณ',
  'pages.notFound': 'ไม่พบเพจนี้',
  'pages.about': 'เกี่ยวกับเพจ',
  'pages.aboutEmpty': 'เพจนี้ยังไม่ได้เขียนแนะนำตัวเองไว้',
  'pages.owner': 'ดูแลโดย',
  'pages.founded': 'ก่อตั้งเมื่อ {when}',
  'pages.shareLink': 'คัดลอกลิงก์',
  'pages.linkCopied': 'คัดลอกลิงก์แล้ว',
  'pages.category.community': 'คอมมูนิตี้',
  'pages.category.language-exchange': 'แลกเปลี่ยนภาษา',
  'pages.category.travel': 'ท่องเที่ยว',
  'pages.category.food': 'อาหาร',
  'pages.category.music': 'ดนตรี',
  'pages.category.gaming': 'เกม',
  'pages.category.study': 'การเรียน',
  'pages.category.business': 'ธุรกิจ',
  'pages.category.art': 'ศิลปะ',
  'pages.category.sport': 'กีฬา',
  'pages.category.technology': 'เทคโนโลยี',
  'pages.category.other': 'อื่นๆ',

  'register.title': 'สร้างบัญชีใหม่',
  'register.usernamePlaceholder': 'ชื่อผู้ใช้',
  'register.emailPlaceholder': 'อีเมล',
  'register.passwordPlaceholder': 'รหัสผ่านใหม่',
  'register.chooseCountry': 'เลือกประเทศ',
  'register.chooseLanguage': 'เลือกภาษา',
  'register.lede': 'ฟรี และใช้เวลาประมาณหนึ่งนาที',
  'verify.title': 'ยืนยันอีเมลของคุณ',
  'verify.lede': 'กดครั้งเดียวก็เสร็จ ที่ต้องกดเองเพราะระบบสแกนอีเมลมักเปิดลิงก์ก่อนคุณ',
  'verify.action': 'ยืนยันอีเมลนี้',
  'verify.createdTitle': 'บัญชีของคุณพร้อมแล้ว',
  'verify.createdLede': 'เข้าสู่ระบบด้วยรหัสผ่านที่ตั้งไว้ แล้วเริ่มใช้งานได้เลย',
  'verify.doneTitle': 'ยืนยันอีเมลแล้ว',
  'verify.doneLede': 'ขอบคุณ อีเมลของคุณได้รับการยืนยันแล้ว',
  'verify.goToApp': 'ไปที่ TalkVibe',
  'verify.failedTitle': 'ลิงก์นี้ใช้ไม่ได้',
  'verify.failed': 'ยืนยันด้วยลิงก์นี้ไม่สำเร็จ',
  'verify.missingToken': 'ลิงก์นี้ไม่มีรหัสยืนยัน',
  'verify.failedHelp': 'ลิงก์ใช้ได้ 24 ชั่วโมง และใช้ได้ครั้งเดียว',
  'verify.startAgain': 'เริ่มใหม่',
  'register.checkInbox': 'ตรวจกล่องอีเมลของคุณ',
  'register.sentTo': 'ส่งลิงก์ยืนยันไปที่ {email} แล้ว เปิดลิงก์เพื่อสร้างบัญชีให้เสร็จ',
  'register.linkExpires': 'ลิงก์ใช้ได้ 24 ชั่วโมง บัญชีจะยังไม่ถูกสร้างจนกว่าจะเปิดลิงก์',
  'register.sendAgain': 'ส่งลิงก์อีกครั้ง',
  'register.badge': 'ขั้นตอนเดียวจบ',
  'register.usernameHelp': '{min}–{max} ตัวอักษร ใช้ได้เฉพาะ a–z ตัวเล็ก ตัวเลข และขีดล่าง',
  'register.usernameFormatOk': 'รูปแบบถูกต้อง เราจะตรวจว่าชื่อนี้ว่างอยู่ตอนกดสมัคร',
  'register.emailHelp': 'เราจะส่งลิงก์ไปที่อีเมลนี้เพื่อยืนยันว่าเป็นของคุณ',
  'register.birthdate': 'วันเกิด',
  'register.birthdateHelp': 'ต้องมีอายุ {min} ปีขึ้นไป คนอื่นจะเห็นแค่วันและเดือน ไม่เห็นปีเกิด',
  'register.displayNameOptional': 'ไม่บังคับ',
  'register.displayNamePlaceholder': 'ชื่อที่คนอื่นเห็น',
  'register.displayNameHelp': 'ถ้าเว้นว่างไว้ เราจะใช้ชื่อผู้ใช้แทน',
  'register.confirmPassword': 'ยืนยันรหัสผ่าน',
  'register.confirmPasswordPlaceholder': 'พิมพ์รหัสผ่านอีกครั้ง',
  'register.passwordMismatch': 'รหัสผ่านสองช่องไม่ตรงกัน',
  'register.rules.title': 'รหัสผ่านต้อง',
  'register.rules.met': '{done} จาก {total}',
  'register.rules.length': 'ยาวอย่างน้อย {count} ตัวอักษร',
  'register.rules.varied': 'ไม่ใช่ตัวอักษรเดียวซ้ำกันทั้งหมด',
  'register.rules.uncommon': 'ไม่ใช่รหัสผ่านที่คนเดากันบ่อย',
  'register.whereTitle': 'คุณอยู่ที่ไหน และอ่านภาษาอะไร',
  'register.termsAgree': 'ฉันยอมรับ{terms}และ{privacy}',
  'register.termsRequired': 'กรุณายอมรับเงื่อนไขก่อนสมัคร',
  'register.submit': 'สร้างบัญชี',
  'register.failed': 'สร้างบัญชีไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
  'recover.title': 'ลืมรหัสผ่านใช่ไหม',
  'recover.lede': 'กรอกอีเมลหรือชื่อผู้ใช้ของบัญชีคุณ แล้วเราจะส่งลิงก์สำหรับตั้งรหัสผ่านใหม่ไปให้',
  'recover.badge': 'กู้คืนบัญชี',
  'recover.identifier': 'อีเมลหรือชื่อผู้ใช้ของบัญชี',
  'recover.identifierHint': 'เช่น name@example.com หรือ @username',
  'recover.submit': 'ส่งลิงก์ตั้งรหัสผ่านใหม่',
  'recover.sending': 'กำลังส่ง…',
  'recover.expiryTitle': 'ลิงก์มีอายุสั้น',
  'recover.expiryBody': 'ใช้ได้ภายใน {count} นาที และใช้ได้ครั้งเดียว อย่าส่งต่อให้ใคร เพราะใครที่เปิดลิงก์นี้ตั้งรหัสผ่านได้เลย',
  'recover.failed': 'ส่งอีเมลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
  'recover.stepsTitle': 'สามขั้นตอน',
  'recover.step1': 'บอกเราว่าคุณคือใคร',
  'recover.step1Body': 'อีเมลหรือชื่อผู้ใช้',
  'recover.step2': 'เปิดกล่องจดหมาย',
  'recover.step2Body': 'กดลิงก์ภายใน {count} นาที',
  'recover.step3': 'ตั้งรหัสผ่านใหม่',
  'recover.step3Body': 'แล้วเข้าสู่ระบบด้วยรหัสนั้น',
  'recover.sentTitle': 'ตรวจกล่องจดหมายของคุณ',
  'recover.sentBody': 'ถ้ามีบัญชีที่ใช้ {identifier} อยู่ ลิงก์กำลังเดินทางไปหา และจะหมดอายุใน {count} นาที',
  'recover.sentSpam': 'รอสักครู่แล้วยังไม่มา ลองดูในโฟลเดอร์สแปม',
  'recover.sentAgain': 'ลองอีเมลอื่น',

  'reset.title': 'ตั้งรหัสผ่านใหม่',
  'reset.lede': 'เลือกรหัสที่คุณจำได้ แล้วเข้าสู่ระบบด้วยรหัสนี้ได้ทันที',
  'reset.submit': 'บันทึกรหัสผ่านใหม่',
  'reset.saving': 'กำลังบันทึก…',
  'reset.signsOut': 'การตั้งรหัสใหม่จะออกจากระบบให้ทุกเครื่องที่เคยเข้าไว้',
  'reset.noToken': 'ลิงก์นี้ไม่มีรหัสยืนยันติดมาด้วย',
  'reset.noTokenAction': 'ขอลิงก์ใหม่',
  'reset.failed': 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ',

  'geo.suggestLabel': 'ข้อเสนอเปลี่ยนภาษา',
  'geo.suggestTitle': 'ดูเหมือนคุณกำลังใช้งานอยู่ใน{country}',
  'geo.suggestBody': 'ต้องการอ่าน TalkVibe เป็น{language}ไหม',
  'geo.suggestAccept': 'เปลี่ยนเป็น{language}',
  'geo.suggestDismiss': 'ไม่ ขอบคุณ',

  'register.sideTitle': 'บัญชีเดียว คุยได้ทุกภาษา',
  'register.sideLede': 'เลือกภาษาที่คุณอ่าน แล้วทุกอย่างที่ส่งถึงคุณจะมาถึงเป็นภาษานั้น',
  'home.popularInterests': 'ความสนใจยอดนิยม',
  'home.peopleToMeet': 'คนที่คุณอาจอยากรู้จัก',
  'home.noSuggestions': 'ยังไม่มีคนแนะนำตอนนี้',

  'post.like': 'ถูกใจ',
  'post.liked': 'ถูกใจแล้ว',
  'post.likes.one': 'ถูกใจ {count}',
  'post.likes.other': 'ถูกใจ {count}',
  'post.comment': 'ความคิดเห็น',
  'post.comments.one': '{count} ความคิดเห็น',
  'post.comments.other': '{count} ความคิดเห็น',
  'post.share': 'แชร์',
  'post.shareCopied': 'คัดลอกลิงก์แล้ว',
  'post.writeComment': 'เขียนความคิดเห็น',
  'post.postComment': 'ส่ง',
  'post.noComments': 'ยังไม่มีความคิดเห็น',
  'post.deletePost': 'ลบโพสต์',
  'post.deleteComment': 'ลบ',
  'post.deleteConfirm': 'ลบรายการนี้ใช่ไหม',
  'post.removed': 'รายการนี้ถูกลบแล้ว',
  'post.signInToComment': 'เข้าสู่ระบบเพื่อแสดงความคิดเห็น',
  'post.loadMoreComments': 'ดูความคิดเห็นเพิ่ม',

  /* Landing page (signed out) */
  'landing.nav.label': 'ผลิตภัณฑ์',
  'landing.nav.features': 'ฟีเจอร์',
  'landing.nav.how': 'ใช้งานยังไง',
  'landing.nav.interests': 'ความสนใจ',
  'landing.footer.label': 'ส่วนท้าย',
  'landing.footer.rights': 'สงวนลิขสิทธิ์',

  'landing.hero.badge': 'คุยข้ามภาษา',
  'landing.hero.title': 'คุยกับคนทั้งโลก โดยไม่ต้องเปลี่ยนภาษาที่คุณคิด',
  'landing.hero.subtitle':
    'ทุกคนพิมพ์ด้วยภาษาที่ตัวเองคิด และอ่านด้วยภาษาของตัวเอง ข้อความต้นฉบับอยู่ตรงนั้นเสมอ กดดูได้ทุกเมื่อ',
  'landing.hero.cta': 'เริ่มใช้งานฟรี',
  'landing.hero.secondary': 'มีบัญชีอยู่แล้ว',

  'landing.stats.languages': '{count} ภาษา',
  'landing.stats.countries': '{count} ประเทศ',
  'landing.stats.interests': 'ความสนใจให้เลือก {count} หมวด',

  'landing.preview.label': 'บทสนทนาเดียว สองภาษา',
  'landing.preview.writes': '{name} ที่ {place} พิมพ์ว่า',
  'landing.preview.reads': '{name} ที่ {place} อ่านว่า',
  'landing.preview.shownIn': 'แสดงเป็น {language}',
  'landing.preview.original': 'เก็บต้นฉบับไว้เสมอ',

  'landing.features.eyebrow': 'ทำไมต้อง TalkVibe',
  'landing.features.title': 'สร้างมาเพื่อคุยกับคนที่ไม่ได้ใช้ภาษาเดียวกับคุณ',
  'landing.features.lede':
    'ที่นี่การแปลไม่ใช่ฟีเจอร์ที่เอามาแปะเพิ่มทีหลัง แต่เป็นเหตุผลที่แอปนี้มีอยู่ และทุกอย่างถูกสร้างล้อมรอบมัน',
  'landing.features.translate.title': 'แปลให้ตั้งแต่ข้อความมาถึง',
  'landing.features.translate.body':
    'ข้อความ โพสต์ และคอมเมนต์ถูกแปลเป็นภาษาที่คุณอ่าน โดยต้นฉบับยังอยู่ข้างใต้เสมอ เพราะคำแปลที่ตรวจสอบไม่ได้คือคำแปลที่เชื่อไม่ได้',
  'landing.features.discover.title': 'หาคน ไม่ใช่ให้อัลกอริทึมเลือกให้',
  'landing.features.discover.body':
    'ค้นจากประเทศ ภาษา หรือความสนใจ การจัดอันดับเขียนไว้ชัดเจนและเปิดให้อ่านได้ ไม่มีคะแนนความนิยมมาตัดสินว่าคุณจะได้เจอใคร',
  'landing.features.spaces.title': 'เพจ กลุ่ม และสตอรี่',
  'landing.features.spaces.body':
    'โพสต์ลงหน้าตัวเอง เปิดเพจสาธารณะ หรือเก็บไว้ในกลุ่ม สตอรี่อยู่ได้หนึ่งวัน ทั้งหมดถูกแปลด้วยวิธีเดียวกัน',
  'landing.features.privacy.title': 'ความเป็นส่วนตัวที่ตั้งค่าได้จริง',
  'landing.features.privacy.body':
    'เลือกได้ว่าใครเห็นโปรไฟล์คุณ บล็อกและรายงานได้ ดาวน์โหลดข้อมูลทั้งหมดที่เรามีเกี่ยวกับคุณได้ และลบบัญชีได้จริง',

  'landing.how.eyebrow': 'ใช้งานยังไง',
  'landing.how.title': 'สามขั้นตอน แล้วเริ่มคุยได้เลย',
  'landing.how.step1.title': 'เลือกภาษาที่คุณอ่าน',
  'landing.how.step1.body': 'ทุกอย่างที่ส่งมาถึงคุณจะมาเป็นภาษานั้น ไม่ว่าต้นทางจะเขียนด้วยภาษาอะไร',
  'landing.how.step2.title': 'หาใครสักคน',
  'landing.how.step2.body': 'จากประเทศ จากภาษาที่เขาพูดได้ หรือจากเรื่องที่คุณสองคนสนใจเหมือนกัน',
  'landing.how.step3.title': 'พิมพ์ได้เลย',
  'landing.how.step3.body': 'พิมพ์ด้วยภาษาคุณ เขาอ่านด้วยภาษาเขา ไม่มีใครต้องเปลี่ยนภาษา',

  'landing.interests.eyebrow': 'หาคนที่ใช่',
  'landing.interests.title': 'เริ่มจากเรื่องที่คุณสนใจ',
  'landing.interests.lede': 'ทุกความสนใจคือทางเข้าไปหาคนที่สนใจเรื่องเดียวกัน',
  'landing.interests.all': 'ดูทั้งหมด',

  'landing.cta.badge': 'ใช้ฟรี ไม่มีอะไรซ่อนหลังค่าบริการ',
  'landing.cta.title': 'พร้อมเจอคนที่พูดคนละภาษากับคุณหรือยัง',
  'landing.cta.body': 'สมัครใช้เวลาไม่ถึงหนึ่งนาที',
  'landing.cta.primary': 'สร้างบัญชี',
  'landing.cta.secondary': 'เข้าสู่ระบบแทน',

  'legal.terms': 'เงื่อนไขการใช้งาน',
  'download.footerLink': 'แอปบนคอมพิวเตอร์',
  'download.title': 'TalkVibe บนคอมพิวเตอร์',
  'download.intro': 'ใช้ฟรี และเป็นบัญชีเดียวกับที่ใช้บนเว็บ',
  'download.why': 'ปิดหน้าต่างได้โดยที่แอปยังคงการเชื่อมต่อไว้ ข้อความที่เข้ามาตอนคุณไม่ได้ดูจึงยังถึงคุณ — รวมถึงแชทกลุ่ม ซึ่งบนเว็บจะไม่แจ้งเตือนเลยถ้าไม่ได้เปิดแท็บไว้',
  'download.notPublished': 'ตัวติดตั้งสร้างและทดสอบแล้ว แต่ยังไม่ได้อยู่บนเซิร์ฟเวอร์ดาวน์โหลด ปุ่มด้านล่างจึงยังกดไม่ได้ และจะใช้ไม่ได้จนกว่าจะอัปโหลดขึ้นไป',
  'download.yourComputer': 'เครื่องของคุณ',
  'download.version': 'เวอร์ชัน {version}',
  'download.noMobile': 'บนมือถือให้ใช้เว็บไซต์ — แอปมือถือยังไม่ได้ปล่อย',
  'download.warnMac': 'macOS จะไม่ยอมเปิดไฟล์นี้ในครั้งแรก เพราะแอปยังไม่ได้เซ็นด้วยใบรับรองของ Apple ให้คลิกขวาที่แอปในโฟลเดอร์ Applications เลือก Open แล้วกด Open อีกครั้งในกล่องที่เด้งขึ้นมา ทำครั้งเดียวพอ',
  'download.warnWindows': 'Windows จะขึ้นกล่องสีน้ำเงิน “Windows protected your PC” เพราะตัวติดตั้งยังไม่ได้เซ็นด้วยใบรับรอง ให้กด More info แล้วกด Run anyway',
  'download.warnLinux': 'AppImage ไม่ต้องติดตั้ง สั่ง chmod +x ให้รันได้แล้วเปิดเลย ส่วนไฟล์ .deb ติดตั้งด้วย apt install ./TalkVibe-*.deb แล้วแอปจะไปอยู่ในเมนู',
  'legal.privacy': 'นโยบายความเป็นส่วนตัว',
  'settings.dangerZone': 'การลบบัญชี',
  'settings.exportData': 'ดาวน์โหลดข้อมูลของคุณ',
  'settings.exportHelp': 'ข้อมูลทั้งหมดที่เรามีเกี่ยวกับคุณ เป็นไฟล์ JSON',
  'settings.deleteAccount': 'ลบบัญชี',
  'settings.deleteHelp': 'จะลบโปรไฟล์ ความสัมพันธ์ และเพจที่คุณเป็นเจ้าของทั้งหมด และย้อนกลับไม่ได้',
  'settings.deleteConfirm': 'พิมพ์รหัสผ่านเพื่อยืนยัน',
  'settings.notifications': 'การแจ้งเตือน',
  'settings.notifyFriendRequest': 'คำขอเป็นเพื่อน',
  'settings.notifyFriendAccepted': 'การตอบรับคำขอ',
  'settings.notifyNewMessage': 'ข้อความใหม่',
  'settings.notifyGroupPost': 'โพสต์ในกลุ่มของคุณ',
  'settings.notifyPostMention': 'มีคนแท็กฉันในโพสต์',
  'settings.notifyProfilePost': 'มีคนเขียนบนโปรไฟล์ฉัน',
  'settings.notifyPostRepost': 'มีคนรีโพสต์โพสต์ฉัน',

  'settings.browserNotifications': 'เด้งเตือนบนเครื่องนี้',
  'settings.browserNotificationsHelpPush':
    'เบราว์เซอร์จะเด้งแจ้งเตือนให้ แม้ตอนที่ปิด TalkVibe ไปแล้ว '
    + 'ตัวเลือกนี้เป็นของเบราว์เซอร์นี้เท่านั้น ไม่ใช่ของบัญชีคุณ',
  'settings.browserNotificationsHelp':
    'เบราว์เซอร์จะเด้งแจ้งเตือนให้ ตอนที่ยังเปิด TalkVibe ค้างไว้อยู่ในแท็บ '
    + 'ตัวเลือกนี้เป็นของเบราว์เซอร์นี้เท่านั้น ไม่ใช่ของบัญชีคุณ',
  'settings.browserNotificationsBlocked':
    'เบราว์เซอร์ของคุณบล็อกการแจ้งเตือนของเว็บนี้ไว้ ต้องไปอนุญาตในการตั้งค่าเว็บไซต์เอง '
    + 'เราขอจากหน้านี้อีกไม่ได้',
  'settings.browserNotificationsUnsupported':
    'เบราว์เซอร์นี้แสดงการแจ้งเตือนไม่ได้ ถ้าใช้ iPhone หรือ iPad ต้องเพิ่ม TalkVibe '
    + 'ลงหน้าโฮมก่อน',
  'settings.browserNotificationsDismissed': 'ยังไม่ได้เลือกอะไร เปิดอีกครั้งเพื่อให้ถามใหม่',
  'settings.browserNotificationsTest': 'ส่งทดสอบ',
  'settings.browserNotificationsTestBody': 'การแจ้งเตือนใช้งานได้แล้ว',

  'feed.title': 'ฟีดของคุณ',
  'feed.empty': 'ยังไม่มีอะไรที่นี่',
  'feed.emptyHelp': 'เขียนอะไรสักอย่างด้านบน ติดตามเพจ หรือเข้ากลุ่มดูสิ',
  'feed.inGroup': 'ใน {name}',
  'post.reply': 'ตอบกลับ',
  'post.replyTo': 'ตอบ {name}',
  'post.replies.one': '{count} การตอบกลับ',
  'post.replies.other': '{count} การตอบกลับ',

  'composer.placeholder': 'อยากแบ่งปันอะไร',
  'nav.seeAllRequests': 'ดูคำขอทั้งหมด',
  'stories.title': 'สตอรี่',
  'stories.add': 'เพิ่มสตอรี่',
  'stories.yours': 'สตอรี่ของคุณ',
  'stories.pickPhoto': 'เลือกรูป',
  'stories.uploading': 'กำลังอัปโหลด…',
  'stories.caption': 'คำบรรยาย',
  'stories.share': 'แชร์',
  'stories.expiryNote': 'สตอรี่จะหายไปใน 24 ชั่วโมง',
  'stories.subtitle': 'แชร์ช่วงเวลาดีๆ รูปถ่าย หรือความคิดของคุณไปยังเพื่อนๆ',
  'stories.ttlBadge': '{count} ชม.',
  'stories.chooseType': 'เลือกรูปแบบสตอรี่ของคุณ',
  'stories.typeMedia': 'สตอรี่รูปภาพ',
  'stories.typeMediaBody': 'อัปโหลดรูปภาพ แล้วใส่คำบรรยายประกอบ',
  'stories.typeText': 'สตอรี่ข้อความ',
  'stories.typeTextBody': 'พิมพ์ข้อความบนพื้นหลังสีสันสดใส',
  'stories.dropzone': 'ลากรูปภาพมาวางที่นี่',
  'stories.dropzoneOr': 'หรือคลิกเพื่อเลือกจากอุปกรณ์ของคุณ',
  'stories.maxSize': 'ไม่เกิน {count} MB',
  'stories.changePhoto': 'เลือกรูปอื่น',
  'stories.yourCaption': 'คำบรรยายของคุณ',
  'stories.captionPlaceholder': 'เขียนอะไรสักหน่อยเกี่ยวกับรูปนี้…',
  'stories.charCount': '{used} / {max} ตัวอักษร',
  'stories.fontStyle': 'สไตล์แบบอักษร',
  'stories.textAlign': 'การจัดวางข้อความ',
  'stories.background': 'พื้นหลัง',
  'stories.stickers': 'เครื่องมือเสริมลูกเล่นสตอรี่',
  'stories.stickerMusic': 'เพิ่มเพลง',
  'stories.stickerLocation': 'ตำแหน่งที่ตั้ง',
  'stories.stickerMention': 'แท็กเพื่อน',
  'stories.stickerPoll': 'โพลล์คำถาม',
  'stories.privacy': 'ใครเห็นได้',
  'stories.privacyPublic': 'ทุกคน',
  'stories.privacyNote': 'สตอรี่จะแสดงให้ทุกคนที่เห็นโปรไฟล์ของคุณได้',
  'stories.settings': 'ตั้งค่าสตอรี่',
  'stories.saveDraft': 'บันทึกแบบร่าง',
  'stories.close': 'ปิด',
  'stories.livePreview': 'ตัวอย่างแบบเรียลไทม์ (9:16)',
  'stories.replyPlaceholder': 'ส่งข้อความตอบกลับ…',
  'stories.justNow': 'เพิ่งเมื่อสักครู่',
  'stories.previewEmpty': 'รูปของคุณจะแสดงตรงนี้',
  'stories.background.dusk': 'สนธยา',
  'stories.background.ocean': 'ทะเล',
  'stories.background.citrus': 'ส้ม',
  'stories.background.forest': 'ป่า',
  'stories.background.rose': 'กุหลาบ',
  'stories.background.ink': 'หมึก',
  'stories.textStyle.plain': 'ปกติ',
  'stories.textStyle.bold': 'หนา',
  'stories.textStyle.light': 'บาง',
  'stories.fontStyleHint': 'ปรับน้ำหนักและระยะตัวอักษร ตัวพิมพ์ใช้ฟอนต์ของเครื่องคุณเสมอ ทุกภาษาจึงแสดงได้ถูกต้อง',
  'stories.textAlign.left': 'ซ้าย',
  'stories.textAlign.center': 'กลาง',
  'stories.textAlign.right': 'ขวา',
  'stories.audience.profile': 'ตามโปรไฟล์',
  'stories.audience.friends': 'เพื่อนเท่านั้น',
  'stories.audience.me': 'เฉพาะฉัน',
  'stories.stickerMusicWhy': 'TalkVibe ยังไม่มีระบบเสียงเลย และคลังเพลงต้องเคลียร์เรื่องลิขสิทธิ์ก่อนจะเขียนโค้ด',
  'stories.placeLabel': 'อยู่ที่ไหน',
  'stories.placePlaceholder': 'แถว BTS อโศก',
  'stories.placeHint': 'เขียนแบบที่จะบอกเพื่อนก็ได้ ระบบนี้ไม่มีแผนที่อยู่ข้างหลัง เขตหรือชื่อสถานีจึงมีประโยชน์กว่าที่อยู่เต็ม',
  'stories.mentionPlaceholder': 'ค้นหาเพื่อนของคุณ…',
  'stories.mentionEmpty': 'ไม่มีใครให้แท็กเพิ่ม แท็กได้เฉพาะเพื่อน',
  'stories.untag': 'เอา {name} ออก',
  'stories.pollQuestion': 'คำถามโพล',
  'stories.pollQuestionPlaceholder': 'ไปไหนกันดี',
  'stories.pollOptionPlaceholder': 'ตัวเลือกที่ {n}',
  'stories.pollAddOption': 'เพิ่มตัวเลือก',
  'stories.pollRemoveOption': 'ลบตัวเลือกนี้',
  'stories.pollIncomplete': 'โพลต้องมีคำถามและตัวเลือกที่ไม่ซ้ำกันอย่างน้อยสองข้อ',
  'stories.draftFound': 'คุณมีสตอรี่ที่ยังไม่ได้โพสต์',
  'stories.draftRestore': 'ทำต่อ',
  'stories.draftDiscard': 'ทิ้งไป',
  'stories.draftNoPhoto': 'ข้อความกลับมาแล้ว แต่รูปไม่ได้ถูกบันทึกไว้ เลือกใหม่อีกครั้ง',
  'stories.previewTextEmpty': 'ข้อความของคุณจะอยู่ที่นี่',
  'stories.remove': 'ลบสตอรี่',
  'stories.removeConfirm': 'ลบสตอรี่นี้ไหม',
  'stories.views': 'ยอดดู',
  'stories.next': 'สตอรี่ถัดไป',
  'stories.previous': 'สตอรี่ก่อนหน้า',
  'stories.loadFailed': 'โหลดสตอรี่ไม่สำเร็จ',
  'profile.posts': 'โพสต์',
  'profile.noPosts': 'ยังไม่มีอะไรที่นี่ สิ่งที่คุณโพสต์ลงโปรไฟล์จะมาแสดงตรงนี้',
  'profile.writeOnWall': 'เขียนบนโปรไฟล์นี้',
  'profile.writeOnWallPlaceholder': 'เขียนอะไรถึง {name}…',
  'profile.writeOnWallHelp': 'ข้อความจะไปอยู่บนโปรไฟล์ของเขา และเขาลบออกได้',
  'pages.slugHelp': 'เพจของคุณจะอยู่ที่ talkvibe.me/p/…',
  'pages.slugTaken': 'ที่อยู่นี้ถูกใช้แล้ว',
  'pages.created': 'สร้างเพจแล้ว',
  'pages.yours': 'เพจของคุณ',
  'pages.none': 'คุณยังไม่มีเพจ',
  'pages.openFeed': 'โพสต์',

  /* Common */
  'common.cancel': 'ยกเลิก',
  'common.close': 'ปิด',
  'common.delete': 'ลบ',
  'common.confirm': 'ยืนยัน',
  'common.loading': 'กำลังโหลด…',
  'common.error': 'เกิดข้อผิดพลาด',
  'common.tryAgain': 'ลองใหม่',
  'common.search': 'ค้นหา',
  'common.required': 'จำเป็น',
  'common.optional': 'ไม่บังคับ',
  'common.justNow': 'เมื่อสักครู่',
};
